import {
	Stack,
	Button,
	Inline,
	Divider,
	Grid,
	Col,
	Heading,
	Text,
	Box,
	Alert,
	Spinner,
	Icons,
	joinAttributes,
	Ping,
	Badge,
	Dropdown,
	Component,
	Table,
	Time,
	Money,
	Select,
	Tabs,
	TabsList,
	TabContent,
	useCallbackRef,
	Container,
	MoneySum,
	TabItem,
	AsyncSelect,
	Tooltip,
} from "@sembark-travel/ui-base"
import { Fragment, ReactNode, useCallback, useEffect, useState } from "react"
import { Omit, Optional } from "utility-types"
import * as Validator from "yup"
import {
	TIntegration,
	TNewIntegrationSchema,
	TPGIntegratedApp,
	TPGInvoice,
	TPGPaymentLink,
	TPGSettlement,
	TPGTransaction,
	TPaymentGatewayIntegration,
	TPaymentMethod,
	useFetchPaymentGatewayIntegratedApp,
	TPGSubAccount,
	usePaymentGatewayIntegratedAppsCount,
} from "./store"
import {
	Form,
	withServerErrors,
	validateFormValues,
	TextInputField,
	SubmissionError,
	isTruthy,
	TextAreaInputField,
	SwitchInputField,
	SelectField,
	DateRangePickerField,
	CheckboxInputField,
	useFieldValue,
} from "@sembark-travel/ui-form"
import { PlatformIcon } from "./PlatformIcon"
import { IListResponse, useXHR } from "@sembark-travel/xhr"
import useSWR from "swr"
import {
	Link,
	NavLink,
	queryToSearch,
	useLocationQuery,
} from "@sembark-travel/ui-router"
import { generatePath } from "../router-utils"
import { JSONPreview } from "./JsonPreview"
import {
	Dialog,
	useCreateConfirmationDialogContext,
	useDialog,
} from "@sembark-travel/ui-dialog"
import {
	CopyToClipboard,
	CopyToClipboardButton,
} from "@sembark-travel/ui-copy-to-clipboard"
import {
	MoneyInputWithTenantCurrenciesField,
	useFunctionalCurrencyOfTenant,
} from "../Currencies"
import {
	areAdvancedFiltersAppliedDefault,
	CursorListView,
	Search,
	TSearchParams,
	useSearch,
} from "@sembark-travel/ui-list"
import {
	addMoney,
	isZeroMoney,
	TFormattedMoney,
	TMoney,
} from "@sembark-travel/money"
import {
	dateToQuery,
	dateToUTCString,
	endOf,
	formatDate,
	parseDate,
	parseDateFromQuery,
	startOf,
	subtractUnit,
} from "@sembark-travel/datetime-utils"
import { Batch, TBatch } from "./Batch"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { Markdown } from "@sembark-travel/ui-markdown"
import pluralize from "pluralize"
import { PERMISSIONS, useCheckPermissions } from "../Auth"
import { IAccount, SelectAccount } from "../Accounting"
import { TemporaryStorage } from "../DownloadManager"
import { SelectHotels } from "../Hotels"

export function PaymentGatewayIntegratedApp({
	integration,
	children,
}: {
	integration: TPaymentGatewayIntegration
	children?: ({
		integration,
	}: {
		integration: TPaymentGatewayIntegration
	}) => React.ReactNode
}) {
	const { integrated_app_id: integratedAppId, in_test_mode_at } = integration
	const { data, error, isValidating, mutate } =
		useFetchPaymentGatewayIntegratedApp(integratedAppId)
	if (!data) {
		if (error) {
			return (
				<Alert status="error">
					{error?.message || "Something went wrong. Please try after sometime."}
				</Alert>
			)
		}
		return <Spinner alignCenter padding="4" />
	}

	const {
		data: integratedApp,
		meta: { exception },
	} = data
	return (
		<Stack gap="8">
			{integration.platform === "pg_payu" ? (
				<PayUPaymentGatewayIntegratedApp
					integration={integration}
					integratedApp={integratedApp}
					exception={exception}
					inTestModeAt={in_test_mode_at}
					isValidating={isValidating}
					onChange={() => mutate()}
				/>
			) : integration.platform === "pg_simulator" ? (
				<PGSimulatorPaymentGatewayIntegratedApp
					integration={integration}
					integratedApp={integratedApp}
					exception={exception}
					inTestModeAt={in_test_mode_at}
					isValidating={isValidating}
					onChange={() => mutate()}
				/>
			) : (
				<Box>Pending Page</Box>
			)}
			<PGSettings app={integratedApp} onChange={() => mutate()} />
			<Box borderWidth="1">
				<Tabs layout="col">
					<TabsList>
						<NavLink
							to={generatePath("/integrations/:integrationId/pg/transactions", {
								integrationId: integration.uid,
							})}
						>
							Transactions
						</NavLink>
						<NavLink
							to={generatePath("/integrations/:integrationId/pg/settlements", {
								integrationId: integration.uid,
							})}
						>
							Settlements
						</NavLink>
						<NavLink
							to={generatePath(
								"/integrations/:integrationId/pg/payment-links",
								{
									integrationId: integration.uid,
								}
							)}
						>
							Payment Links
						</NavLink>
					</TabsList>
					<TabContent>
						<Box>{children?.({ integration })}</Box>
					</TabContent>
				</Tabs>
			</Box>
		</Stack>
	)
}

function PayUPaymentGatewayIntegratedApp({
	integration,
	integratedApp,
	exception,
	inTestModeAt,
	isValidating,
	onChange,
}: {
	integration: TIntegration
	integratedApp: TPGIntegratedApp
	exception?: Record<string, unknown>
	inTestModeAt?: string | null
	isValidating: boolean
	onChange: () => void
}) {
	const [
		isSubAccountsDialogOpen,
		openSubAccountsDialog,
		closeSubAccountsDialog,
	] = useDialog()
	const subAccountsCount = integratedApp.sub_accounts?.length || 0
	const xhr = useXHR()

	return (
		<Stack gap="8" cursor={isValidating ? "wait" : undefined}>
			<Inline justifyContent="between" flexWrap="wrap">
				<Inline gap="4">
					<Box paddingTop="1">
						<PlatformIcon platform={integration.platform} size="36" />
					</Box>
					<Stack gap="1">
						<Heading>
							PayU Integration {isValidating ? <Icons.Refresh spin /> : null}
						</Heading>
						<Box color="muted" fontSize="sm">
							{joinAttributes(
								<Badge>
									MID:{" "}
									{integratedApp.pg_provider === "payu"
										? integratedApp.payu.merchant_id
										: ""}
								</Badge>,
								<Badge warning={Boolean(inTestModeAt)} success={!inTestModeAt}>
									{inTestModeAt ? "Test Mode" : "Live Mode"}
								</Badge>,
								subAccountsCount ? (
									<Button inline onClick={openSubAccountsDialog}>
										Sub Accounts ({subAccountsCount})
									</Button>
								) : null,
								<LinkedBankAccountingAccount
									integratedApp={integratedApp}
									onChange={onChange}
								/>
							)}
						</Box>
					</Stack>
				</Inline>
				<Inline gap="4" justifyContent="end">
					<Box>
						<Dropdown alignRight>
							<Dropdown.ToggleButton level="tertiary">
								<Icons.DotsVertical />
							</Dropdown.ToggleButton>
							<Dropdown.Menu>
								<NavLink
									to={generatePath(
										"/integrations/:integrationId/pg/configuration",
										{
											integrationId: integration.uid.toString(),
										}
									)}
								>
									Configuration
								</NavLink>
								<Dropdown.MenuItem
									onClick={async (e: React.MouseEvent) => {
										e.preventDefault()
										try {
											await xhr.post(
												`integrations/pg/${integratedApp.id}/sub-accounts/sync`
											)
											showSnackbar("Sub accounts synced successfully.")
											onChange()
										} catch (e) {
											const error = e as Error
											showSnackbar(
												error.message ||
													"Failed to sync sub accounts. Please try again."
											)
										}
									}}
								>
									Sync Sub Accounts
								</Dropdown.MenuItem>
								<Dropdown.MenuItemDivider />
								<NavLink
									to={generatePath(
										"/integrations/:integrationId/payu/:appId/update-credentials",
										{
											integrationId: integration.uid.toString(),
											appId: integratedApp.id.toString(),
										}
									)}
								>
									Update Credentials
								</NavLink>
							</Dropdown.Menu>
						</Dropdown>
					</Box>
				</Inline>
			</Inline>
			{exception ? (
				<Stack gap="4">
					<Stack gap="1">
						<Heading fontSize="md" color="danger">
							<Icons.Attention size="6" /> Communication Failure{" "}
							<Ping color="danger" />
						</Heading>
						<Text color="muted">
							There are some issues in communications with PayU. Please contact
							our support or try to reconnect with your account to fix any
							issues.
						</Text>
					</Stack>
					<JSONPreview
						data={exception}
						bgColor="danger"
						color="danger"
						borderColor="danger"
					/>
				</Stack>
			) : null}
			{isSubAccountsDialogOpen ? (
				<PGSubAccountsDialog
					app={integratedApp}
					open={isSubAccountsDialogOpen}
					onClose={closeSubAccountsDialog}
					onChange={onChange}
				/>
			) : null}
		</Stack>
	)
}

function PGSimulatorPaymentGatewayIntegratedApp({
	integration,
	integratedApp,
	exception,
	inTestModeAt,
	isValidating,
	onChange,
}: {
	integration: TIntegration
	integratedApp: TPGIntegratedApp
	exception?: Record<string, unknown>
	inTestModeAt?: string | null
	isValidating: boolean
	onChange: () => void
}) {
	const [
		isSubAccountsDialogOpen,
		openSubAccountsDialog,
		closeSubAccountsDialog,
	] = useDialog()
	const subAccountsCount = integratedApp.sub_accounts?.length || 0

	return (
		<Stack gap="8" cursor={isValidating ? "wait" : undefined}>
			<Inline justifyContent="between" flexWrap="wrap">
				<Inline gap="4">
					<Box paddingTop="1">
						<PlatformIcon platform={integration.platform} size="36" />
					</Box>
					<Stack gap="1">
						<Heading>
							Payment Gateway Simulator{" "}
							{isValidating ? <Icons.Refresh spin /> : null}
						</Heading>
						<Box fontSize="sm">
							{joinAttributes(
								<Badge warning={Boolean(inTestModeAt)} success={!inTestModeAt}>
									{inTestModeAt ? "Test Mode" : "Live Mode"}
								</Badge>,
								subAccountsCount ? (
									<Button inline onClick={openSubAccountsDialog}>
										Sub Accounts ({subAccountsCount})
									</Button>
								) : null,
								<LinkedBankAccountingAccount
									integratedApp={integratedApp}
									onChange={onChange}
								/>
							)}
						</Box>
					</Stack>
				</Inline>
			</Inline>
			{exception ? (
				<Stack gap="4">
					<Stack gap="1">
						<Heading fontSize="md" color="danger">
							<Icons.Attention size="6" /> Communication Failure{" "}
							<Ping color="danger" />
						</Heading>
						<Text color="muted">
							There are some issues in communications with PayU. Please contact
							our support or try to reconnect with your account to fix any
							issues.
						</Text>
					</Stack>
					<JSONPreview
						data={exception}
						bgColor="danger"
						color="danger"
						borderColor="danger"
					/>
				</Stack>
			) : null}
			{isSubAccountsDialogOpen ? (
				<PGSubAccountsDialog
					app={integratedApp}
					open={isSubAccountsDialogOpen}
					onClose={closeSubAccountsDialog}
					onChange={onChange}
				/>
			) : null}
		</Stack>
	)
}

function LinkedBankAccountingAccount({
	integratedApp,
	onChange,
}: {
	integratedApp: TPGIntegratedApp
	onChange: () => void
}) {
	const [isDialogOpen, openDialog, closeDialog] = useDialog()
	const xhr = useXHR()
	return (
		<>
			<Inline inline gap="2">
				<Box>
					{integratedApp.bank_accounting_account ? (
						<Link
							to={generatePath("/accounting/accounts/:accountId", {
								accountId: integratedApp.bank_accounting_account.id.toString(),
							})}
							color="accent"
						>
							{integratedApp.bank_accounting_account.name}
						</Link>
					) : (
						<Button inline onClick={openDialog}>
							Link Bank Account
						</Button>
					)}
				</Box>
				<Button inline onClick={openDialog}>
					<Icons.Pencil />
				</Button>
			</Inline>
			<Dialog
				open={isDialogOpen}
				onClose={closeDialog}
				title="Updated Linked Bank Account"
				sm
			>
				<Form<{
					accounting_account?: IAccount
					integrated_app: typeof integratedApp
				}>
					initialValues={{
						accounting_account: integratedApp.bank_accounting_account,
						integrated_app: integratedApp,
					}}
					onSubmit={withServerErrors(async (values) => {
						await xhr.post(
							`integrations/pg/${values.integrated_app.id}/bank-accounting-account`,
							{
								accounting_account: values.accounting_account?.id,
							}
						)
						showSnackbar("Account linked successfully.")
						closeDialog()
						onChange()
					})}
					subscription={{ submitting: true }}
				>
					{({ handleSubmit, submitting }) => (
						<form onSubmit={handleSubmit} noValidate>
							<Dialog.Body>
								<Stack gap="4">
									<Text>
										Please select the accounting account, linked with the bank
										account assoicated in your Payment Gateway Dashboard.
									</Text>
									<SelectField
										name="accounting_account"
										label="Select Account"
										select={SelectAccount}
										params={{ entity_type: "tenants" }}
										fetchOnMount
									/>
									<Text>
										On settlements, the credit/debit transactions will be
										automatically created to this account. You can then utilize
										this account for your day-to-day accounting purpose.
									</Text>
								</Stack>
								<SubmissionError />
							</Dialog.Body>
							<Dialog.Footer>
								<Button type="submit" disabled={submitting}>
									{submitting ? "Saving..." : "Save Details"}
								</Button>
								<Button onClick={closeDialog}>Cancel</Button>
							</Dialog.Footer>
						</form>
					)}
				</Form>
			</Dialog>
		</>
	)
}

export function PaymentGatewayIntegratedAppConfiguration({
	webhookUrls,
}: {
	webhookUrls: Array<{ name: string; url: string }>
}) {
	return (
		<Stack gap="4">
			<Stack gap="1">
				<Heading fontSize={"md"}>Webhook</Heading>
				<Text color="muted">
					Please setup following webhooks in your PayU dashboard to enable
					real-time S2S.
				</Text>
			</Stack>
			<Stack gap="2">
				{webhookUrls.map(({ name, url }) => (
					<Stack gap="1" key={name}>
						<Text fontWeight="semibold">{name}</Text>
						<CopyToClipboardTextInput value={url} />
					</Stack>
				))}
			</Stack>
		</Stack>
	)
}

function PGSettings({
	app,
	onChange,
}: {
	app: TPGIntegratedApp
	onChange: () => void
}) {
	const { id, payment_methods, pg_provider } = app

	const [
		isEditingPaymentMethods,
		openEditPaymentMethodsDialog,
		closeEditPaymentMethodsDialog,
	] = useDialog()
	const xhr = useXHR()
	return (
		<>
			{/**
			 * <Inline gap="8" flexWrap="wrap">
			 * // NOTE: After enabling, be sure the check the updation dialog
			 *	 <Stack gap="2">
			 *		<Inline gap="2">
			 *			<Text fontSize="md" fontWeight="semibold">
			 *				Payment Methods
			 *			</Text>
			 *			<Button inline onClick={openEditPaymentMethodsDialog}>
			 *				<Icons.Pencil color="muted" />
			 *			</Button>
			 *		</Inline>
			 *		<Box>
			 *			{payment_methods && payment_methods?.length ? (
			 *				<Inline gap="2">
			 *					{(payment_methods || []).map((method) => (
			 *						<Badge key={method.id}>{method.name}</Badge>
			 *					))}
			 *				</Inline>
			 *			) : (
			 *				<Text>All Methods</Text>
			 *			)}
			 *		</Box>
			 *	 </Stack>
			 * </Inline>
			 */}
			<Dialog
				open={isEditingPaymentMethods}
				onClose={closeEditPaymentMethodsDialog}
				title="Edit Enabled Payment Methods"
				sm
			>
				<Dialog.Body>
					<UpdatePaymentMethodsForm
						provider={pg_provider}
						onCancel={closeEditPaymentMethodsDialog}
						initialValues={{ payment_methods }}
						onSubmit={async (data) => {
							await xhr.post(`integrations/pg/${id}/payment-methods`, {
								payment_methods: data.payment_methods.map((p) => p.id),
							})
							closeEditPaymentMethodsDialog()
							// TODO: Remove it when we enable the payment methods editing
							openEditPaymentMethodsDialog()
							onChange()
						}}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

function PGSubAccountsDialog({
	app,
	open,
	onClose,
	onChange,
}: {
	app: TPGIntegratedApp
	open: boolean
	onClose: () => void
	onChange: () => void
}) {
	const xhr = useXHR()
	const subAccounts = app.sub_accounts || []

	return (
		<Dialog open={open} onClose={onClose} title="Sub Accounts" lg>
			<Dialog.Body>
				<Stack gap="4">
					<Text color="muted">
						Link active hotels to each payment gateway sub account.
					</Text>
					{subAccounts.length ? (
						<Stack gap="3">
							{subAccounts.map((subAccount: TPGSubAccount) => (
								<Box key={subAccount.id} borderWidth="1" padding="4">
									<Grid gap="4">
										<Col xs={12} md={6}>
											<Stack gap="1">
												<Text fontWeight="semibold">
													{subAccount.display_name || "MISSING NAME"}
												</Text>
												<Text color="muted" fontSize="sm">
													{subAccount.email || "No email provided"}
												</Text>
												<Box>
													{subAccount.is_verified === false ? (
														<Badge warning>Unverified</Badge>
													) : null}
												</Box>
											</Stack>
										</Col>
										<Col xs>
											<Component initialState={false}>
												{({ state: isEditing, setState: setIsEditing }) =>
													!isEditing ? (
														<Inline gap="4" alignItems="center">
															<Stack gap="1">
																<Inline gap="2">
																	<Text color="muted" fontSize="sm">
																		Hotel
																	</Text>
																	{subAccount.hotel ? (
																		<Button
																			onClick={() => setIsEditing(true)}
																			inline
																		>
																			<Icons.Pencil size="3" />
																		</Button>
																	) : null}
																</Inline>
																{subAccount.hotel ? (
																	<Link
																		to={generatePath("/hotels/:hotelId", {
																			hotelId: subAccount.hotel.id.toString(),
																		})}
																		color="accent"
																	>
																		<Stack fontWeight="semibold">
																			{subAccount.hotel.name}
																		</Stack>
																		<Text fontSize="sm" color="muted">
																			{subAccount.hotel.location?.name}
																		</Text>
																	</Link>
																) : (
																	<>
																		<Button onClick={() => setIsEditing(true)}>
																			Map with Hotel
																		</Button>
																	</>
																)}
															</Stack>
														</Inline>
													) : (
														<Form<{
															hotel?: TPGSubAccount["hotel"]
														}>
															initialValues={{
																hotel: subAccount.hotel || undefined,
															}}
															onSubmit={withServerErrors(async (values) => {
																await xhr.post(
																	`integrations/pg/${app.id}/sub-accounts/${subAccount.id}/hotel`,
																	{
																		hotel_id: values.hotel?.id || null,
																	}
																)
																showSnackbar(
																	values.hotel
																		? "Hotel linked successfully."
																		: "Hotel unlinked successfully."
																)
																setIsEditing(false)
																onChange()
															})}
															subscription={{ submitting: true }}
														>
															{({ handleSubmit, submitting }) => (
																<form onSubmit={handleSubmit} noValidate>
																	<Stack gap="4">
																		<SelectField
																			name="hotel"
																			label="Select Hotel to map with Account"
																			select={SelectHotels}
																			multiple={false}
																			fetchOnMount
																			clearable
																		/>
																		<SubmissionError />
																		<Inline gap="2">
																			<Button
																				type="submit"
																				disabled={submitting}
																				size="sm"
																			>
																				{submitting ? "Saving..." : "Save"}
																			</Button>
																			<Button
																				disabled={submitting}
																				size="sm"
																				onClick={() => setIsEditing(false)}
																			>
																				Cancel
																			</Button>
																		</Inline>
																	</Stack>
																</form>
															)}
														</Form>
													)
												}
											</Component>
										</Col>
									</Grid>
								</Box>
							))}
						</Stack>
					) : (
						<Box borderWidth="1" padding="4">
							<Text color="muted">No sub accounts found.</Text>
						</Box>
					)}
				</Stack>
			</Dialog.Body>
			<Dialog.Footer>
				<Button onClick={onClose}>Close</Button>
			</Dialog.Footer>
		</Dialog>
	)
}

function useCreatePGIntegrationPayload(
	provider: TPGIntegratedApp["pg_provider"]
) {
	const xhr = useXHR()
	const { data } = useSWR(`integrations/${provider}/create`, () =>
		xhr
			.get<{
				data: {
					payment_methods: Array<{ id: string; name: string }>
					in_test_mode: boolean
				} & {
					payu?: {
						merchant_id: string
					}
				}
			}>(`/integrations/pg/create`, {
				params: {
					provider,
				},
			})
			.then((resp) => resp.data.data)
	)
	return data
}

type TNewPayUIntegrationSchema = TNewIntegrationSchema & {
	payu: {
		api_key: string
		api_salt_v1: string
		api_salt_v2: string
		client_id: string
		client_secret: string
		payment_methods?: Array<{ id: string; name: string }>
		accounting_account?: IAccount
	}
}

const newPayUIntegrationSchemaValidator = Validator.object().shape({
	name: Validator.string()
		.required("Please give a name to your integration")
		.max(50, "Please use 50 or fewer characters for the name"),
	description: Validator.string()
		.required("Please provide an description for this integration")
		.max(500, "Please use 500 or fewer characters for the description"),
	payu: Validator.object().shape({
		api_key: Validator.string().required(
			"Please provide the API Key from your PayU Dashboard"
		),
		api_salt_v1: Validator.string().required(
			"Please provide the API Salt- 32 bit from your PayU Dashboard"
		),
		api_salt_v2: Validator.string().nullable(),
		client_id: Validator.string().required(
			"Please provide the client id from your PayU Dashboard"
		),
		client_secret: Validator.string().required(
			"Please provide the account secret from your PayU Dashboard"
		),
		payment_methods: Validator.array().nullable(),
	}),
})

export function NewPayUIntegrationForm({
	onSubmit,
	onCancel,
	initialValues: propInitialValues,
}: {
	initialValues?: Optional<TNewPayUIntegrationSchema>
	onSubmit: (
		data: Omit<TNewPayUIntegrationSchema, "payu"> & {
			payu: Omit<
				TNewPayUIntegrationSchema["payu"],
				"payment_methods" | "accounting_account"
			> & {
				payment_methods?: Array<string>
				accounting_account?: number | undefined
			}
		}
	) => Promise<unknown>
	onCancel: () => void
}) {
	const [newIntegrationInitialValues] = useState<TNewPayUIntegrationSchema>(
		() => ({
			name: propInitialValues?.name || "",
			description: propInitialValues?.description || "",
			abilities: propInitialValues?.abilities || [],
			platform: "pg_payu" as const,
			in_test_mode: "0",
			payu: {
				api_key: "",
				api_salt_v1: "",
				api_salt_v2: "",
				client_id: "",
				client_secret: "",
				payment_methods: [],
				accounting_account: undefined,
			},
		})
	)
	const data = useCreatePGIntegrationPayload("payu")
	const isInTestMode = isTruthy(data?.in_test_mode)
	const {
		confirm,
		isConfirmationVisible: isTnCConfirmationVisible,
		reject: rejectTnC,
		accept: acceptTnC,
	} = useCreateConfirmationDialogContext()
	const {
		hasScrolledToEnd,
		progress,
		ref: containerRef,
	} = useScrollableContainer()
	return (
		<>
			<Form<TNewPayUIntegrationSchema>
				initialValues={newIntegrationInitialValues}
				validate={validateFormValues(newPayUIntegrationSchemaValidator)}
				onSubmit={withServerErrors(async (values) => {
					if (!(await confirm())) {
						throw new Error("Please accept the terms to continue")
					}
					await onSubmit({
						...values,
						in_test_mode: isInTestMode ? 1 : 0,
						payu: {
							...values.payu,
							accounting_account: values.payu.accounting_account?.id,
							payment_methods:
								values.payu.payment_methods?.map((p) => p.id) || [],
						},
					})
				})}
				subscription={{ submitting: true }}
			>
				{({ submitting, handleSubmit }) => (
					<form noValidate onSubmit={handleSubmit}>
						<Stack gap="4">
							<Grid gap="6">
								<Col sm={12} md={4} position="relative">
									<Stack gap="2" position="sticky" top="0">
										<Inline gap="4">
											<Box>
												<PlatformIcon platform="pg_payu" size="40" />
											</Box>
											<Heading>PayU Integration Credentials</Heading>
										</Inline>
										<Text>
											<Text
												href="https://docs.payu.in/docs/get-client-id-and-secret-from-dashboard"
												as="a"
												rel="noopener noreferrer"
												target="_blank"
												color="accent"
												textDecoration="underline"
											>
												Click here to follow the official documentations
											</Text>{" "}
											on how to get Client ID and Secret from your PayU
											Dashboard.
										</Text>
										<Text>
											<Badge success={!isInTestMode} warning={isInTestMode}>
												MID: {data?.payu?.merchant_id || "..."}
											</Badge>
										</Text>
									</Stack>
								</Col>
								<Col>
									<Stack gap="4">
										<TestLiveModeInfo isInTestMode={isInTestMode} />
										<PayUCredentialsInputField isInTestMode={isInTestMode} />
									</Stack>
								</Col>
							</Grid>
							<Divider />
							<Grid gap="6">
								<Col sm={12} md={4} position="relative">
									<Stack gap="2" position="sticky" top="0">
										<Heading>Settlement Accounting Account</Heading>
										<Text>
											Select an accounting account, which is linked to your bank
											account from gateway, to store the settled transactions.
										</Text>
									</Stack>
								</Col>
								<Col>
									<Stack gap="4">
										<SelectField
											name="payu.accounting_account"
											label="Select Bank Accounting Account"
											select={SelectAccount}
											params={{ entity_type: "tenants" }}
											fetchOnMount
											help="On settlements, the credit/debit transactions will be automatically created to this account. You can then utilize this account for your day-to-day accounting purpose"
										/>
									</Stack>
								</Col>
							</Grid>
							{/**
							 * <Divider />
							 * <Grid gap="6">
							 * 	<Col sm={12} md={4}>
							 * 		<Stack gap="2">
							 * 			<Heading>Payment Methods</Heading>
							 * 			<Text>
							 * 				Please select the payment methods/modes what you want to
							 * 				enable for this integration.
							 * 			</Text>
							 * 		</Stack>
							 * 	</Col>
							 * 	<Col>
							 * 		<Stack gap="4">
							 * 			<SelectPaymentMethodField
							 * 				name="payu.payment_methods"
							 * 				paymentMethods={data?.payment_methods || []}
							 * 			/>
							 * 		</Stack>
							 * 	</Col>
							 * </Grid>
							 * */}
							<Divider />
							<Grid gap="6">
								<Col sm={12} md={4}>
									<Stack gap="2">
										<Heading>Integration Details</Heading>
										<Text>
											Please provide a name and description for this
											integration.
										</Text>
									</Stack>
								</Col>
								<Col>
									<Stack gap="4">
										<TextInputField
											label="Name"
											name="name"
											type="text"
											required
											autoComplete="false"
											placeholder="e.g. My Integration, Marketing Site"
										/>
										<TextInputField
											label="Description"
											name="description"
											type="text"
											required
											autoComplete="false"
											placeholder="e.g. Automate enquiry creation by bringing leads from our marketing site into Sembark."
											help="Some description or overview of your integration"
										/>
									</Stack>
								</Col>
							</Grid>
							<Divider sm />
							<Grid gap="0">
								<Col xs={12} sm={{ offset: 4, span: 8 }}>
									<Stack gap="4" paddingLeft={{ sm: "6" }}>
										<SubmissionError />
										<Inline gap="4">
											<Button type="submit" disabled={submitting}>
												{submitting ? "Please wait..." : "Save Details"}
											</Button>
											<Button disabled={submitting} onClick={onCancel}>
												Cancel
											</Button>
										</Inline>
									</Stack>
								</Col>
							</Grid>
						</Stack>
					</form>
				)}
			</Form>
			<Dialog open={isTnCConfirmationVisible} onClose={rejectTnC}>
				<Dialog.Header>
					<Dialog.Title>
						Terms & Conditions for Payment Gateway Integration
					</Dialog.Title>
				</Dialog.Header>
				<Dialog.Body>
					<Form
						initialValues={{ check_1: 0, check_2: 0, check_3: 0 }}
						onSubmit={withServerErrors(async (values) => {
							if (
								!isTruthy(values.check_1) ||
								!isTruthy(values.check_2) ||
								!isTruthy(values.check_3)
							) {
								throw new Error(
									"Please accept all the checkbox options to continue."
								)
							}
							acceptTnC()
						})}
					>
						{({ handleSubmit }) => (
							<form noValidate onSubmit={handleSubmit}>
								<Stack gap="4">
									<Text>
										By accessing or using our payment gateway integration
										services, you acknowledge and agree to be bound by these
										Terms & Conditions. If you do not agree with any part of
										these terms, you should not use our integration services.
										Our role is limited to providing technology solutions for
										integrating with third-party payment gateways, and your
										continued use constitutes acceptance of this limitation.
									</Text>
									<Box position="relative" rounded="md" overflow="hidden">
										<Box
											style={{ maxHeight: "400px" }}
											overflow="auto"
											padding="4"
											borderWidth="2"
											rounded="md"
											bgColor="inset"
											ref={containerRef}
										>
											<Markdown>{TnC}</Markdown>
										</Box>
										<Box
											style={{
												height: "30px",
												opacity: (100 - progress) / 100,
												background:
													"linear-gradient(to bottom, transparent, black)",
											}}
											position="absolute"
											left="0"
											bottom="0"
											right="0"
										></Box>
										<Box
											position="absolute"
											bottom="0"
											left="0"
											width="full"
											style={{
												transformOrigin: "left center",
												transform: `scaleX(${progress / 100})`,
												height: "4px",
											}}
											bgColor="primary_emphasis"
										></Box>
									</Box>
									<Text>
										We may update or amend these Terms & Conditions from time to
										time to reflect changes in our services, industry practices,
										or legal requirements. The updated version will be effective
										once published on this page. By continuing to use our
										integration services after any changes, you agree to the
										revised Terms & Conditions.
									</Text>
									{!hasScrolledToEnd ? (
										<Alert inline status="error">
											<Text fontWeight="semibold">
												Please read the full terms before continuing.
											</Text>
										</Alert>
									) : null}
									<Stack
										gap="2"
										padding="4"
										borderWidth="1"
										rounded="md"
										opacity={hasScrolledToEnd ? "100" : "70"}
										bgColor={hasScrolledToEnd ? "primary" : "default"}
									>
										<CheckboxInputField
											name="check_1"
											label="I have read and agree to the Terms & Conditions for Payment Gateway Integrations."
											disabled={!hasScrolledToEnd}
										/>
										<CheckboxInputField
											name="check_2"
											label="By proceeding, I agree to the Terms & Conditions and confirm my understanding of Sembark Tech Private Limited's limited role as a technology provider."
											disabled={!hasScrolledToEnd}
										/>
										<CheckboxInputField
											name="check_3"
											label="I accept the Terms & Conditions and understand that disputes, settlements, and refunds must be handled directly with my Payment Gateway Provider."
											disabled={!hasScrolledToEnd}
										/>
									</Stack>
								</Stack>
								<Divider sm />
								<Stack gap="4">
									<SubmissionError />
									<Inline gap="4">
										<Button type="submit" disabled={!hasScrolledToEnd}>
											I Agree, Please Proceed.
										</Button>
										<Button onClick={rejectTnC} level="tertiary">
											Cancel
										</Button>
									</Inline>
								</Stack>
							</form>
						)}
					</Form>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

type TNewPGSimulatorIntegrationSchema = TNewIntegrationSchema & {
	pg_simulator: {
		api_key: string
		payment_methods?: Array<{ id: string; name: string }>
		accounting_account?: IAccount
	}
}

const newPGSimulatorIntegrationSchemaValidator = Validator.object().shape({
	name: Validator.string()
		.required("Please give a name to your integration")
		.max(50, "Please use 50 or fewer characters for the name"),
	description: Validator.string()
		.required("Please provide an description for this integration")
		.max(500, "Please use 500 or fewer characters for the description"),
	pg_simulator: Validator.object().shape({
		api_key: Validator.string().required("Please provide any random API keys"),
	}),
})

export function NewPGSimulatorIntegrationForm({
	onSubmit,
	onCancel,
	initialValues: propInitialValues,
}: {
	initialValues?: Optional<TNewPGSimulatorIntegrationSchema>
	onSubmit: (
		data: Omit<TNewPGSimulatorIntegrationSchema, "pg_simulator"> & {
			pg_simulator: Omit<
				TNewPGSimulatorIntegrationSchema["pg_simulator"],
				"payment_methods" | "accounting_account"
			> & {
				payment_methods?: Array<string>
				accounting_account?: number | undefined
			}
		}
	) => Promise<unknown>
	onCancel: () => void
}) {
	const [newIntegrationInitialValues] =
		useState<TNewPGSimulatorIntegrationSchema>(() => ({
			name: propInitialValues?.name || "",
			description: propInitialValues?.description || "",
			abilities: propInitialValues?.abilities || [],
			platform: "pg_simulator" as const,
			in_test_mode: "0",
			pg_simulator: {
				api_key: "",
				payment_methods: [],
				accounting_account: undefined,
			},
		}))
	const data = useCreatePGIntegrationPayload("pg_simulator")
	const isInTestMode = isTruthy(data?.in_test_mode)
	const {
		confirm,
		isConfirmationVisible: isTnCConfirmationVisible,
		reject: rejectTnC,
		accept: acceptTnC,
	} = useCreateConfirmationDialogContext()
	const {
		hasScrolledToEnd,
		progress,
		ref: containerRef,
	} = useScrollableContainer()
	return (
		<>
			<Form<TNewPGSimulatorIntegrationSchema>
				initialValues={newIntegrationInitialValues}
				validate={validateFormValues(newPGSimulatorIntegrationSchemaValidator)}
				onSubmit={withServerErrors(async (values) => {
					if (!(await confirm())) {
						throw new Error("Please accept the terms to continue")
					}
					await onSubmit({
						...values,
						in_test_mode: isInTestMode ? 1 : 0,
						pg_simulator: {
							...values.pg_simulator,
							accounting_account: values.pg_simulator.accounting_account?.id,
							payment_methods:
								values.pg_simulator.payment_methods?.map((p) => p.id) || [],
						},
					})
				})}
				subscription={{ submitting: true }}
			>
				{({ submitting, handleSubmit }) => (
					<form noValidate onSubmit={handleSubmit}>
						<Stack gap="4">
							<Grid gap="6">
								<Col sm={12} md={4} position="relative">
									<Stack gap="2" position="sticky" top="0">
										<Inline gap="4">
											<Box>
												<PlatformIcon platform="pg_simulator" size="40" />
											</Box>
											<Heading>Simulator Credentials</Heading>
										</Inline>
										<Text>
											Please provide any fake credentials for Payment Gateway
										</Text>
									</Stack>
								</Col>
								<Col>
									<Stack gap="4">
										<TextInputField
											name="pg_simulator.api_key"
											label="API Key"
											placeholder="e.g. sembarktesting"
											required
										/>
										<SelectField
											name="pg_simulator.accounting_account"
											label="Select Bank Accounting Account"
											select={SelectAccount}
											params={{ entity_type: "tenants" }}
											fetchOnMount
											help="On settlements, the credit/debit transactions will be automatically created to this account. You can then utilize this account for your day-to-day accounting purpose"
										/>
									</Stack>
								</Col>
							</Grid>
							{/**
							 * <Divider />
							 * <Grid gap="6">
							 * 	<Col sm={12} md={4}>
							 * 		<Stack gap="2">
							 * 			<Heading>Payment Methods</Heading>
							 * 			<Text>
							 * 				Please select the payment methods/modes what you want to
							 * 				enable for this integration.
							 * 			</Text>
							 * 		</Stack>
							 * 	</Col>
							 * 	<Col>
							 * 		<Stack gap="4">
							 * 			<SelectPaymentMethodField
							 * 				name="pg_simulator.payment_methods"
							 * 				paymentMethods={data?.payment_methods || []}
							 * 			/>
							 * 		</Stack>
							 * 	</Col>
							 * </Grid>
							 * */}
							<Divider />
							<Grid gap="6">
								<Col sm={12} md={4}>
									<Stack gap="2">
										<Heading>Integration Details</Heading>
										<Text>
											Please provide a name and description for this
											integration.
										</Text>
									</Stack>
								</Col>
								<Col>
									<Stack gap="4">
										<TextInputField
											label="Name"
											name="name"
											type="text"
											required
											autoComplete="false"
											placeholder="e.g. My Integration, Marketing Site"
										/>
										<TextInputField
											label="Description"
											name="description"
											type="text"
											required
											autoComplete="false"
											placeholder="e.g. Automate enquiry creation by bringing leads from our marketing site into Sembark."
											help="Some description or overview of your integration"
										/>
									</Stack>
								</Col>
							</Grid>
							<Divider sm />
							<Grid gap="0">
								<Col xs={12} sm={{ offset: 4, span: 8 }}>
									<Stack gap="4" paddingLeft={{ sm: "6" }}>
										<SubmissionError />
										<Inline gap="4">
											<Button type="submit" disabled={submitting}>
												{submitting ? "Please wait..." : "Save Details"}
											</Button>
											<Button disabled={submitting} onClick={onCancel}>
												Cancel
											</Button>
										</Inline>
									</Stack>
								</Col>
							</Grid>
						</Stack>
					</form>
				)}
			</Form>
			<Dialog open={isTnCConfirmationVisible} onClose={rejectTnC}>
				<Dialog.Header>
					<Dialog.Title>
						Terms & Conditions for Payment Gateway Integration
					</Dialog.Title>
				</Dialog.Header>
				<Dialog.Body>
					<Form
						initialValues={{ check_1: 0, check_2: 0, check_3: 0 }}
						onSubmit={withServerErrors(async (values) => {
							if (
								!isTruthy(values.check_1) ||
								!isTruthy(values.check_2) ||
								!isTruthy(values.check_3)
							) {
								throw new Error(
									"Please accept all the checkbox options to continue."
								)
							}
							acceptTnC()
						})}
					>
						{({ handleSubmit }) => (
							<form noValidate onSubmit={handleSubmit}>
								<Stack gap="4">
									<Text>
										By accessing or using our payment gateway integration
										services, you acknowledge and agree to be bound by these
										Terms & Conditions. If you do not agree with any part of
										these terms, you should not use our integration services.
										Our role is limited to providing technology solutions for
										integrating with third-party payment gateways, and your
										continued use constitutes acceptance of this limitation.
									</Text>
									<Box position="relative" rounded="md" overflow="hidden">
										<Box
											style={{ maxHeight: "400px" }}
											overflow="auto"
											padding="4"
											borderWidth="2"
											rounded="md"
											bgColor="inset"
											ref={containerRef}
										>
											<Markdown>{TnC}</Markdown>
										</Box>
										<Box
											style={{
												height: "30px",
												opacity: (100 - progress) / 100,
												background:
													"linear-gradient(to bottom, transparent, black)",
											}}
											position="absolute"
											left="0"
											bottom="0"
											right="0"
										></Box>
										<Box
											position="absolute"
											bottom="0"
											left="0"
											width="full"
											style={{
												transformOrigin: "left center",
												transform: `scaleX(${progress / 100})`,
												height: "4px",
											}}
											bgColor="primary_emphasis"
										></Box>
									</Box>
									<Text>
										We may update or amend these Terms & Conditions from time to
										time to reflect changes in our services, industry practices,
										or legal requirements. The updated version will be effective
										once published on this page. By continuing to use our
										integration services after any changes, you agree to the
										revised Terms & Conditions.
									</Text>
									{!hasScrolledToEnd ? (
										<Alert inline status="error">
											<Text fontWeight="semibold">
												Please read the full terms before continuing.
											</Text>
										</Alert>
									) : null}
									<Stack
										gap="2"
										padding="4"
										borderWidth="1"
										rounded="md"
										opacity={hasScrolledToEnd ? "100" : "70"}
										bgColor={hasScrolledToEnd ? "primary" : "default"}
									>
										<CheckboxInputField
											name="check_1"
											label="I have read and agree to the Terms & Conditions for Payment Gateway Integrations."
											disabled={!hasScrolledToEnd}
										/>
										<CheckboxInputField
											name="check_2"
											label="By proceeding, I agree to the Terms & Conditions and confirm my understanding of Sembark Tech Private Limited's limited role as a technology provider."
											disabled={!hasScrolledToEnd}
										/>
										<CheckboxInputField
											name="check_3"
											label="I accept the Terms & Conditions and understand that disputes, settlements, and refunds must be handled directly with my Payment Gateway Provider."
											disabled={!hasScrolledToEnd}
										/>
									</Stack>
								</Stack>
								<Divider sm />
								<Stack gap="4">
									<SubmissionError />
									<Inline gap="4">
										<Button type="submit" disabled={!hasScrolledToEnd}>
											I Agree, Please Proceed.
										</Button>
										<Button onClick={rejectTnC} level="tertiary">
											Cancel
										</Button>
									</Inline>
								</Stack>
							</form>
						)}
					</Form>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

function useScrollableContainer() {
	const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false)
	const [progress, setProgress] = useState(0) // % of scroll completed
	const [ref, setRef] = useCallbackRef<HTMLElement>()

	const setContainerRef: typeof setRef = useCallback(
		(ref) => {
			// reset the boolean
			setHasScrolledToEnd(false)
			setRef(ref)
		},
		[setRef, setHasScrolledToEnd]
	)

	useEffect(() => {
		const el = ref
		if (!el) return

		function handleScroll() {
			if (!el) return
			const { scrollTop, scrollHeight, clientHeight } = el
			const scrolled = scrollTop + clientHeight
			const progressPercent = Math.min((scrolled / scrollHeight) * 100, 100)

			setProgress(progressPercent)
			setHasScrolledToEnd(
				(hasScrolledToEnd) => hasScrolledToEnd || scrolled >= scrollHeight - 5 // allow small buffer
			)
		}

		el.addEventListener("scroll", handleScroll)
		handleScroll() // call once to setup everything

		return () => {
			el.removeEventListener("scroll", handleScroll)
		}
	}, [ref])

	return { hasScrolledToEnd, progress, ref: setContainerRef }
}

const TnC = `### 1. Scope of Services

We act solely as a technology provider offering integration with third-party payment gateway providers (e.g., PayU). All services provided by us will be as per our internal policies. We may discontinue any of the services. If you do not wish to use our Integration service due to such incidents, We will refund the unused duration subscription amount.  Our current services include:

- Generation of payment links for packages and instalments.
- Reports on payment links, transactions, and settlements.
- API integration and related support.

 We will provide these integration services through limited payment gateway providers. Custom requirements will be chargeable separately.

### 2. No Involvement in Payment Processing

We do not collect, process, store, or handle payments, funds, or settlements in any manner. All payment collection, processing, refunds, chargebacks, settlements, or transaction disputes are managed exclusively by the respective payment gateway provider.

### 3. Responsibility of Clients

- Clients are responsible for maintaining an active account with their chosen payment gateway provider.
- Any queries, disputes, or issues regarding deductions, settlements, refunds, or transaction failures must be raised directly with the payment gateway provider or the assigned account manager.
- You should use the Integration service for all of your payments within Sembark to maximize the digitization benefits. Since we can provide this integration service to limited companies, We may review integration usage from time to time and If usage is for less than 95% installments, we may consider revoking this service by giving a refund for unused duration.

### 4. Limitation of Liability

- We shall not be held liable for any payment failures, delays in settlement, incorrect deductions, chargebacks, or disputes between clients, their customers and their payment gateway provider.
- Our responsibility is limited to ensuring proper technical integration and providing the features outlined under Section 1.

### 5. Data & Reporting

- Reports generated by our system are for reference only and may not reflect real-time data.
- Official data must be verified directly with the payment gateway provider.

### 6. System Availability

While we strive to ensure smooth functioning of our integration services, we do not guarantee uninterrupted availability. Downtime due to maintenance, internal issues, or force majeure events may occur.

### 7. Compliance

Clients are responsible for complying with all applicable laws, regulations, and policies of the government and the payment gateway provider while using our integration services.

### 8. Data Privacy Notice

Your client personal details are shared with your payment gateway provider through their APIs from Sembark. Please make sure you and your payment gateway provider follow all rules and regulations related to data privacy.

### 9. Disclaimer

We expressly disclaim any responsibility for financial transactions or outcomes thereof. Our role is limited to technology facilitation of payment gateway integrations.`

type TUpdatePayUIntegrationCredentialsSchema = {
	payu: {
		api_key: string
		api_salt_v1: string
		api_salt_v2: string
		client_id: string
		client_secret: string
	}
}

const updatePayUIntegrationCredentialsSchemaValidator =
	Validator.object().shape({
		payu: Validator.object().shape({
			api_key: Validator.string().required(
				"Please provide the API Key from your PayU Dashboard"
			),
			api_salt_v1: Validator.string().required(
				"Please provide the API Salt- 32 bit from your PayU Dashboard"
			),
			api_salt_v2: Validator.string().nullable(),
			client_id: Validator.string().required(
				"Please provide the client id from your PayU Dashboard"
			),
			client_secret: Validator.string().required(
				"Please provide the account secret from your PayU Dashboard"
			),
		}),
	})

export function UpdatePayUIntegrationCredentialsForm({
	onSubmit,
	onCancel,
	initialValues: propInitialValues,
	isInTestMode,
}: {
	initialValues?: Optional<
		Omit<TUpdatePayUIntegrationCredentialsSchema, "payu">
	> & {
		payu?: Optional<TUpdatePayUIntegrationCredentialsSchema["payu"]>
	}
	onSubmit: (data: TUpdatePayUIntegrationCredentialsSchema) => Promise<unknown>
	onCancel: () => void
	isInTestMode: boolean
}) {
	isInTestMode = isTruthy(isInTestMode)
	const [initialValues] = useState<TUpdatePayUIntegrationCredentialsSchema>(
		() => ({
			payu: {
				api_key: "",
				api_salt_v1: "",
				api_salt_v2: "",
				client_id: "",
				client_secret: "",
				...propInitialValues?.payu,
			},
		})
	)
	return (
		<Form<TUpdatePayUIntegrationCredentialsSchema>
			initialValues={initialValues}
			validate={validateFormValues(
				updatePayUIntegrationCredentialsSchemaValidator
			)}
			onSubmit={withServerErrors(async (values) => {
				await onSubmit({
					...values,
				})
			})}
			subscription={{ submitting: true }}
		>
			{({ submitting, handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="4">
						<Stack gap="2">
							<Inline gap="4">
								<Box>
									<PlatformIcon platform="pg_payu" size="40" />
								</Box>
								<Heading>PayU Integration Credentials</Heading>
							</Inline>
							<Text>
								<Text
									href="https://docs.payu.in/docs/get-client-id-and-secret-from-dashboard"
									as="a"
									rel="noopener noreferrer"
									target="_blank"
									color="accent"
									textDecoration="underline"
								>
									Click here to follow the official documentations
								</Text>{" "}
								on how to get Client ID and Secret from your PayU Dashboard.
							</Text>
						</Stack>
						<Stack gap="4">
							<TestLiveModeInfo isInTestMode={isInTestMode} />
							<PayUCredentialsInputField
								isInTestMode={isTruthy(isInTestMode)}
							/>
						</Stack>
						<SubmissionError />
						<Divider sm />
						<Inline gap="4">
							<Button type="submit" disabled={submitting}>
								{submitting ? "Please wait..." : "Save Details"}
							</Button>
							<Button disabled={submitting} onClick={onCancel}>
								Cancel
							</Button>
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}

type TUpdatePaymentMethodsFormSchema = {
	payment_methods: Array<TPaymentMethod>
}

export function UpdatePaymentMethodsForm({
	onSubmit,
	onCancel,
	initialValues: propInitialValues,
	provider,
}: {
	initialValues?: Optional<TUpdatePaymentMethodsFormSchema>
	onSubmit: (data: TUpdatePaymentMethodsFormSchema) => Promise<unknown>
	onCancel: () => void
	provider: TPGIntegratedApp["pg_provider"]
}) {
	const [initialValues] = useState<TUpdatePaymentMethodsFormSchema>(() => ({
		payment_methods: propInitialValues?.payment_methods || [],
	}))
	const data = useCreatePGIntegrationPayload(provider)
	return (
		<Form<TUpdatePaymentMethodsFormSchema>
			initialValues={initialValues}
			onSubmit={withServerErrors(async (values) => {
				await onSubmit({ payment_methods: values.payment_methods || [] })
			})}
			subscription={{ submitting: true }}
		>
			{({ submitting, handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="4">
						<SelectPaymentMethodField
							name="payment_methods"
							paymentMethods={data?.payment_methods || []}
						/>
						<Divider sm />
						<SubmissionError />
						<Inline gap="4">
							<Button type="submit" disabled={submitting}>
								{submitting ? "Please wait..." : "Save Details"}
							</Button>
							<Button disabled={submitting} onClick={onCancel}>
								Cancel
							</Button>
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}

function TestLiveModeInfo({ isInTestMode }: { isInTestMode: boolean }) {
	isInTestMode = isTruthy(isInTestMode)
	return (
		<Alert
			status={isInTestMode ? "warning" : "success"}
			title={isInTestMode ? "You are in Test Mode" : "You are in Live Mode"}
		>
			Please ensure your Payment Gateway account is also in{" "}
			<b>{isInTestMode ? "Test" : "Live"} Mode</b> because the credentials will
			vary based on your mode.
		</Alert>
	)
}

function CopyToClipboardTextInput({ value }: { value: string }) {
	return (
		<CopyToClipboard>
			{({ copyNodeTextToClipboard, nodeToCopy }) => (
				<Inline
					maxWidth="full"
					bgColor="subtle"
					borderWidth="1"
					rounded="md"
					overflow="hidden"
					alignItems="center"
				>
					<Box
						flex="1"
						style={{
							maxWidth: "100%",
							overflow: "auto",
						}}
						paddingY="2"
						paddingX="4"
					>
						<Text ref={nodeToCopy as never} whiteSpace="preserve">
							{value}
						</Text>
					</Box>
					<Box paddingX="2">
						<CopyToClipboardButton
							onClick={copyNodeTextToClipboard}
							size="sm"
							iconOnly
						>
							<Icons.ClipboardCopy />
						</CopyToClipboardButton>
					</Box>
				</Inline>
			)}
		</CopyToClipboard>
	)
}

type TPaymentLinkFilters = TSearchParams & {
	active?: 1
}

function filterToLocationQuery(
	filters: TPaymentLinkFilters
): TPaymentLinkFilters {
	const { q, active } = filters
	const query: TPaymentLinkFilters = {}
	if (q) {
		query.q = q
	}
	if (active) {
		query.active = 1
	}
	return query
}

export function PaymentLinksList({ appId }: { appId?: number }) {
	const xhr = useXHR()
	const [query, setQuery] = useLocationQuery<TPaymentLinkFilters>({
		toQuery: filterToLocationQuery,
		fromQuery: filterToLocationQuery,
	})
	const [params, setParams] = useSearch<TPaymentLinkFilters>(query)
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])
	const [
		isGenerateTestLinkDialogOpen,
		openGenerateTestLinkDialog,
		closeGenerateTestLinkDialog,
	] = useDialog()
	const [
		isGenerateTestLinkForExistingBookingsDialogOpen,
		openGenerateTestLinkForExistingBookingsDialogOpen,
		closeGenerateTestLinkForExistingBookingsDialogOpen,
	] = useDialog()
	return (
		<Fragment>
			<Search
				initialParams={params}
				onSearch={(params) => setParams({ ...params, cursor: null })}
				title="Payment Links"
				placeholder="Search by links or description..."
				Filters={PaymentLinkFilters}
				areAdvancedFiltersApplied={(params) => {
					const { active, ...others } = params
					return isTruthy(active) || areAdvancedFiltersAppliedDefault(others)
				}}
				actions={
					appId ? (
						<Inline gap="4">
							<Box>
								<Dropdown alignRight>
									<Dropdown.ToggleButton>
										<Icons.DotsVertical />
									</Dropdown.ToggleButton>
									<Dropdown.Menu>
										<Dropdown.MenuItem
											onClick={(e: React.MouseEvent) => {
												e.preventDefault()
												openGenerateTestLinkDialog()
											}}
										>
											Generate Test Payment Link
										</Dropdown.MenuItem>
										<Dropdown.MenuItemDivider />
										<Dropdown.MenuItem
											onClick={(e: React.MouseEvent) => {
												e.preventDefault()
												openGenerateTestLinkForExistingBookingsDialogOpen()
											}}
										>
											Generate Links for Existing Bookings
										</Dropdown.MenuItem>
									</Dropdown.Menu>
								</Dropdown>
							</Box>
						</Inline>
					) : null
				}
			>
				<Box>
					<CursorListView<TPGPaymentLink>
						pageKey={`pg/${appId}/payment-links`}
						params={params}
						fetch={(xhr, params) =>
							xhr
								.get(`/pg-payment-links`, {
									params: {
										...params,
										app_ids: appId ? [appId] : [],
									},
								})
								.then((resp) => resp.data)
						}
						onCursorChange={(cursor) => {
							setParams({ ...params, cursor })
						}}
					>
						{({ items, refresh }) => (
							<Table
								bordered
								striped
								responsive
								headers={[
									"Description",
									"Amount",
									"Link",
									"For",
									"Created On",
									"",
								]}
								alignCols={{ 1: "right", 5: "right" }}
								rows={items.map((link) => [
									link.invoice?.description,
									link.invoice ? (
										<Money
											money={link.invoice.original_amount}
											showCurrency
											keepTrailingZero
										/>
									) : null,
									link.invoice?.paid_at ? (
										<Badge success>
											Paid: <Time timestamp={link.invoice.paid_at} />
										</Badge>
									) : link.invoice?.link ? (
										<Inline alignItems="center" gap="1">
											<Text
												style={{ maxWidth: "200px" }}
												textOverflow="truncate"
											>
												{link.invoice.link.replace(/^https?:\/\//, "")}
											</Text>
											<Inline>
												<CopyToClipboard>
													{({ copyTextToClipboard }) => (
														<CopyToClipboardButton
															onClick={() =>
																copyTextToClipboard(link.invoice?.link || "")
															}
															inline
															iconOnly
														>
															<Icons.ClipboardCopy />
														</CopyToClipboardButton>
													)}
												</CopyToClipboard>
											</Inline>
										</Inline>
									) : null,

									link.invoice ? (
										link.invoice.invoiceable_type === "given_quotes" ? (
											<Link
												to={generatePath("/trips/:tripId/quotes/:quoteId", {
													tripId:
														link.invoice.invoiceable.trip_quote.trip_id.toString(),
													quoteId:
														link.invoice.invoiceable.trip_quote_id.toString(),
												})}
												color="accent"
												fontWeight="semibold"
											>
												Trip #{link.invoice.invoiceable.trip_quote.trip_id}
											</Link>
										) : link.invoice.invoiceable_type === "instalments" ? (
											<Link
												to={generatePath(
													"/payments/instalments/:instalmentId",
													{
														instalmentId:
															link.invoice.invoiceable.id.toString(),
													}
												)}
												color="accent"
												fontWeight="semibold"
											>
												Instalment #{link.invoice.invoiceable_id}
											</Link>
										) : (
											<Text></Text>
										)
									) : null,
									<Time timestamp={link.created_at} />,
									<Inline gap="2" justifyContent="end">
										{!link.is_active ||
										link.deleted_at ||
										link.invoice?.paid_at ? (
											<Box>
												<Badge warning>Inactive</Badge>
											</Box>
										) : link.invoice
												?.unverified_successful_transactions_count ? (
											<Badge primary>
												<Icons.Clock /> Payment in process...
											</Badge>
										) : (
											<ArchivePGPaymentLinkInDialog
												paymentLink={link}
												onSuccess={() => refresh()}
											>
												{({ archive }) => (
													<Dropdown alignRight>
														<Dropdown.ToggleButton size="sm">
															<Icons.DotsVertical />
														</Dropdown.ToggleButton>
														<Dropdown.Menu>
															<Dropdown.MenuItem
																onClick={(e: React.MouseEvent) => {
																	e.preventDefault()
																	archive()
																}}
															>
																<Icons.Trash /> Delete
															</Dropdown.MenuItem>
														</Dropdown.Menu>
													</Dropdown>
												)}
											</ArchivePGPaymentLinkInDialog>
										)}
									</Inline>,
								])}
							/>
						)}
					</CursorListView>
				</Box>
			</Search>
			{appId ? (
				<>
					<Dialog
						open={isGenerateTestLinkDialogOpen}
						onClose={closeGenerateTestLinkDialog}
						title="Generate Test Payment Link"
						sm
					>
						<Dialog.Body>
							<Stack gap="4">
								<Text>
									Please provide basic details to generate payment link.
								</Text>
								<GeneratePaymentLink
									appId={appId}
									onCancel={() => closeGenerateTestLinkDialog()}
								/>
							</Stack>
						</Dialog.Body>
					</Dialog>
					<Dialog
						open={isGenerateTestLinkForExistingBookingsDialogOpen}
						onClose={closeGenerateTestLinkForExistingBookingsDialogOpen}
						title="Generate Payment Links for Existing Bookings"
						sm
					>
						<Form<unknown>
							onSubmit={withServerErrors(async () => {
								const message = await xhr
									.post<{
										data: { message: string }
									}>(
										"integrations/pg/generate-payment-links-for-existing-entities"
									)
									.then((resp) => resp.data.data.message)
								showSnackbar(message)
								closeGenerateTestLinkForExistingBookingsDialogOpen()
							})}
							subscription={{ submitting: true }}
						>
							{({ handleSubmit, submitting }) => (
								<form onSubmit={handleSubmit}>
									<Dialog.Body>
										<Stack gap="4">
											<Text>
												This will generate payment links for all due
												instalments.
											</Text>
											<SubmissionError />
										</Stack>
									</Dialog.Body>
									<Dialog.Footer>
										<Button type="submit" disabled={submitting}>
											{submitting ? "Initiating..." : "Generate Links"}
										</Button>
										<Button disabled={submitting}>Cancel</Button>
									</Dialog.Footer>
								</form>
							)}
						</Form>
					</Dialog>
				</>
			) : null}
		</Fragment>
	)
}

function PaymentLinkFilters() {
	return (
		<Stack gap="4">
			<SwitchInputField name="active" label="Active Only" />
		</Stack>
	)
}

function GeneratePaymentLink({
	appId,
	onCancel,
}: {
	appId: number
	onCancel?: () => void
}) {
	const xhr = useXHR()
	const functionalCurrency = useFunctionalCurrencyOfTenant()
	const [initialValues] = useState(() => ({
		description: "",
		currency: functionalCurrency.currency,
		amount: "1",
	}))
	type TGeneratedLink = {
		link: string
		expiry_date: string
		amount: TFormattedMoney
	}
	const [generatedLink, setGeneratedLink] = useState<TGeneratedLink | null>(
		null
	)
	return (
		<Form<typeof initialValues>
			initialValues={initialValues}
			onSubmit={withServerErrors(async (values) => {
				setGeneratedLink(null)
				const resp = await xhr
					.post<{
						data: TGeneratedLink
					}>(`/integrations/pg/${appId}/generate-test-payment-link`, values)
					.then((resp) => resp.data)
				setGeneratedLink(resp.data)
			})}
			subscription={{ submitting: true }}
		>
			{({ handleSubmit, submitting }) => (
				<form onSubmit={handleSubmit}>
					<Stack gap="4">
						<MoneyInputWithTenantCurrenciesField
							name="amount"
							currencyFieldName="currency"
							label="Amount"
						/>
						<TextAreaInputField
							name="description"
							label="Link Description"
							placeholder="e.g. Collect Payment for Testing Purpose"
						/>
						<Divider sm />
						<SubmissionError />
						{generatedLink ? (
							<Alert title="Link Generated Successfully">
								<Stack gap="4">
									<Inline gap="4" flexWrap="wrap">
										<Stack gap="1">
											<Text>Amount</Text>
											<Text fontWeight="semibold">
												<Money
													money={generatedLink.amount}
													showCurrency
													keepTrailingZero
												/>
											</Text>
										</Stack>
										<Stack gap="1">
											<Text>Valid Till</Text>
											<Text fontWeight="semibold">
												<Time
													timestamp={generatedLink.expiry_date}
													dateFormat
													timeFormat
												/>
											</Text>
										</Stack>
									</Inline>
									<Stack gap="1">
										<Text>Test Link</Text>
										<Text fontWeight="semibold">
											<CopyToClipboardTextInput value={generatedLink.link} />
										</Text>
									</Stack>
								</Stack>
							</Alert>
						) : null}
						<Inline gap="4">
							<Button type="submit" disabled={submitting}>
								{submitting ? "Generating..." : "Generate Test Link"}
							</Button>
							{onCancel ? (
								<Button
									onClick={() => onCancel()}
									disabled={submitting}
									level="tertiary"
								>
									Cancel
								</Button>
							) : null}
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}

export function LatestPGInvoiceLink({
	paymentInvoice,
	onChange,
	showAmount,
	onRegenerate,
	regenerationLabel,
}: {
	paymentInvoice: TPGInvoice
	onChange?: () => void
	onRegenerate?: () => void
	showAmount?: boolean
	regenerationLabel?: React.ReactNode
}) {
	const link = paymentInvoice.link
	return (
		<CopyToClipboard>
			{({ copyTextToClipboard }) => (
				<Inline gap="2" alignItems="center">
					{showAmount ? (
						paymentInvoice.original_currency !== paymentInvoice.currency ? (
							<Inline gap="1">
								<Money
									money={paymentInvoice.original_amount}
									showCurrency
									fontWeight="semibold"
								/>
								<Tooltip content="Using current conversion rate. Exact rate will be revalidated during payment.">
									<Box color="muted" fontSize="sm">
										(<Money money={paymentInvoice.amount} showCurrency />)
									</Box>
								</Tooltip>
							</Inline>
						) : (
							<Money
								money={paymentInvoice.original_amount}
								showCurrency
								fontWeight="semibold"
							/>
						)
					) : null}
					{paymentInvoice.unverified_successful_transactions?.length ||
					paymentInvoice.unverified_successful_transactions_count ? (
						<Button
							disabled
							size="sm"
							status="warning"
							title="Payment was successful but the transaction could not be verified."
						>
							<Icons.Clock /> Payment in process...
						</Button>
					) : (
						<>
							<CopyToClipboardButton
								onClick={() => copyTextToClipboard(link)}
								size="sm"
								level="secondary"
								status="primary"
							>
								<Icons.ClipboardCopy /> Copy Link
							</CopyToClipboardButton>
							<RegenratePGLinkWithDeleteDialog
								paymentInvoice={paymentInvoice}
								onChange={onChange}
								onRegenerate={onRegenerate}
								regenerationLabel={regenerationLabel}
							/>
						</>
					)}
				</Inline>
			)}
		</CopyToClipboard>
	)
}

export function RegenratePGLinkWithDeleteDialog({
	paymentInvoice,
	onChange,
	onRegenerate,
	regenerationLabel,
}: {
	paymentInvoice: TPGInvoice
	onChange?: () => void
	onRegenerate?: () => void
	regenerationLabel?: React.ReactNode
}) {
	return (
		<Inline gap="2" alignItems="center">
			{onChange ? (
				<Box>
					<ArchivePGInvoiceInDialog
						invoice={paymentInvoice}
						onSuccess={() => {
							onChange()
						}}
					>
						{({ archive }) => (
							<Dropdown alignRight>
								<Dropdown.ToggleButton
									level="tertiary"
									size="sm"
									style={{
										verticalAlign: "middle",
									}}
								>
									<Icons.DotsVertical />
								</Dropdown.ToggleButton>
								<Dropdown.Menu>
									{onRegenerate ? (
										<Dropdown.MenuItem
											onClick={(e) => {
												e.preventDefault()
												onRegenerate()
											}}
										>
											{regenerationLabel || (
												<>
													<Icons.Refresh /> Regenerate
												</>
											)}
										</Dropdown.MenuItem>
									) : null}
									<Dropdown.MenuItem
										onClick={(e) => {
											e.preventDefault()
											archive()
										}}
										color="danger"
									>
										<Icons.Trash /> Delete
									</Dropdown.MenuItem>
								</Dropdown.Menu>
							</Dropdown>
						)}
					</ArchivePGInvoiceInDialog>
				</Box>
			) : null}
		</Inline>
	)
}

function ArchivePGPaymentLinkInDialog({
	paymentLink,
	onSuccess,
	children,
	onCancel,
}: {
	paymentLink: TPGPaymentLink
	onSuccess: () => void
	onCancel?: () => void
	children: (props: { archive: () => void }) => React.ReactNode
}) {
	const xhr = useXHR()
	const [isOpen, openDialog, closeDialog] = useDialog()
	return (
		<>
			{children({ archive: openDialog })}
			<Dialog
				title="Deactivate and Archive Payment Link"
				open={isOpen}
				onClose={() => closeDialog()}
				sm
			>
				<Dialog.Body>
					<Form
						initialValues={{ uid: paymentLink.uid }}
						onSubmit={withServerErrors(async (values) => {
							await xhr.delete(`/pg-payment-links/${values.uid}`)
							onSuccess()
							closeDialog()
						})}
						subscription={{ submitting: true }}
					>
						{({ handleSubmit, submitting }) => (
							<form onSubmit={handleSubmit}>
								<Stack gap="4">
									<Alert status="warning">
										Are you sure you want to disable and archive this link?
									</Alert>
									<SubmissionError />
									<Divider marginY="0" />
									<Inline gap="4">
										<Button
											type="submit"
											disabled={submitting}
											status="warning"
										>
											{submitting
												? "Please wait..."
												: "Yes, Deactivate and Archive"}
										</Button>
										<Button
											onClick={() => {
												onCancel?.()
												closeDialog()
											}}
											disabled={submitting}
										>
											No
										</Button>
									</Inline>
								</Stack>
							</form>
						)}
					</Form>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

function ArchivePGInvoiceInDialog({
	invoice,
	onSuccess,
	children,
	onCancel,
}: {
	invoice: TPGInvoice
	onSuccess: () => void
	onCancel?: () => void
	children: (props: { archive: () => void }) => React.ReactNode
}) {
	const xhr = useXHR()
	const [isOpen, openDialog, closeDialog] = useDialog()
	return (
		<>
			{children({ archive: openDialog })}
			<Dialog
				title="Deactivate and Archive Payment Link"
				open={isOpen}
				onClose={() => closeDialog()}
				sm
			>
				<Dialog.Body>
					<Form
						initialValues={{ uid: invoice.uid }}
						onSubmit={withServerErrors(async (values) => {
							await xhr.delete(`/pg-invoices/${values.uid}`)
							onSuccess()
							closeDialog()
						})}
						subscription={{ submitting: true }}
					>
						{({ handleSubmit, submitting }) => (
							<form onSubmit={handleSubmit}>
								<Stack gap="4">
									<Alert status="warning">
										Are you sure you want to disable and archive this link?
									</Alert>
									<SubmissionError />
									<Divider marginY="0" />
									<Inline gap="4">
										<Button
											type="submit"
											disabled={submitting}
											status="warning"
										>
											{submitting
												? "Please wait..."
												: "Yes, Deactivate and Archive"}
										</Button>
										<Button
											onClick={() => {
												onCancel?.()
												closeDialog()
											}}
											disabled={submitting}
										>
											No
										</Button>
									</Inline>
								</Stack>
							</form>
						)}
					</Form>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

function PayUCredentialsInputField({
	isInTestMode,
}: {
	isInTestMode: boolean
}) {
	return (
		<Stack gap="4">
			<Stack gap="4" padding="4" borderWidth="1" rounded="md" bgColor="inset">
				<Text
					as="a"
					fontWeight="semibold"
					color="primary"
					textDecoration="underline"
					href="https://docs.payu.in/docs/generate-merchant-key-and-salt-on-payu-dashboard"
					target="_blank"
					rel="noopener noreferrer"
				>
					API Key Salt details <Icons.ArrowTopRightOnSquare size="3" />
				</Text>
				<TextInputField
					label="Key"
					secondaryLabel={isTruthy(isInTestMode) ? "Test Mode" : undefined}
					name="payu.api_key"
					type="text"
					required
					autoComplete="false"
					placeholder="e.g. 131114111511151"
				/>
				<TextInputField
					label="Salt- 32 bit"
					secondaryLabel={isTruthy(isInTestMode) ? "Test Mode" : undefined}
					name="payu.api_salt_v1"
					type="text"
					required
					autoComplete="false"
					placeholder="e.g. 131114111511151"
				/>
				<TextInputField
					label="Salt- 256 bit"
					secondaryLabel={
						isTruthy(isInTestMode) ? "Test Mode - Optional" : "Optional"
					}
					name="payu.api_salt_v2"
					type="text"
					autoComplete="false"
					placeholder="e.g. 131114111511151"
				/>
			</Stack>
			<Stack gap="4" padding="4" borderWidth="1" rounded="md" bgColor="inset">
				<Text
					as="a"
					fontWeight="semibold"
					color="primary"
					textDecoration="underline"
					href="https://docs.payu.in/docs/get-client-id-and-secret-from-dashboard"
					target="_blank"
					rel="noopener noreferrer"
				>
					Client ID &amp; Client secret details{" "}
					<Icons.ArrowTopRightOnSquare size="3" />
				</Text>
				<TextInputField
					label="Client ID"
					secondaryLabel={isTruthy(isInTestMode) ? "Test Mode" : undefined}
					name="payu.client_id"
					type="text"
					required
					autoComplete="false"
					placeholder="e.g. 131114111511151"
				/>
				<TextInputField
					label="Client Secret"
					secondaryLabel={isTruthy(isInTestMode) ? "Test Mode" : undefined}
					name="payu.client_secret"
					type="text"
					required
					autoComplete="false"
					placeholder="e.g. dd1dd3dddd123123dd4ddd12312322dd"
				/>
			</Stack>
		</Stack>
	)
}

function SelectPaymentMethodField({
	name,
	paymentMethods,
}: {
	name: string
	paymentMethods: Array<{ id: string; name: string }>
}) {
	return (
		<SelectField
			select={Select}
			name={name}
			options={paymentMethods}
			multiple
			label="Enabled Payment Methods"
			fullWidth
		/>
	)
}

type TTenant = {
	id: number
	name: string
}

type TTransactionFilters = TSearchParams & {
	successful_only?: 1
	after?: Date
	before?: Date
	active_tab: "today" | "yesterday" | "this_month" | "last_month" | "all"
	tenant?: TTenant
}

type TTransactionFiltersInQuery = TSearchParams & {
	successful_only?: 1
	after?: string
	before?: string
	active_tab?: TTransactionFilters["active_tab"]
	tenant?: string
}

function transactionFiltersToQuery(
	filter: TTransactionFilters
): TTransactionFiltersInQuery {
	const { q, page, successful_only, after, before, active_tab, tenant } = filter
	const query: TTransactionFiltersInQuery = { active_tab }
	if (q) {
		query.q = q
	}
	if (page) {
		query.page = page
	}
	if (successful_only) {
		query.successful_only = 1
	}
	if (active_tab === "all") {
		if (after) {
			query.after = dateToQuery(after)
		}
		if (before) {
			query.before = dateToQuery(before)
		}
	}

	if (tenant) {
		query.tenant = `${tenant.id}_${tenant.name}`
	}
	return query
}

function transactionFiltersFromQuery(
	query: TTransactionFiltersInQuery
): TTransactionFilters {
	const { q, page, successful_only, after, before, active_tab, tenant } = query
	const filters: TTransactionFilters = { active_tab: active_tab || "today" }

	if (q) {
		filters.q = q
	}
	if (page) {
		filters.page = page
	}
	if (isTruthy(successful_only)) {
		filters.successful_only = 1
	}
	if (query.active_tab === "all") {
		if (after) {
			filters.after = parseDateFromQuery(after)
		}
		if (before) {
			filters.before = parseDateFromQuery(before)
		}
	}
	if (tenant) {
		const [id, ...name] = tenant.split("_")
		filters.tenant = {
			id: Number(id),
			name: name.join("_"),
		}
	}
	return filters
}

export function TransactionsList({ appId }: { appId?: number }) {
	const [query, setQuery] = useLocationQuery<
		TTransactionFilters,
		TTransactionFiltersInQuery
	>({
		toQuery: transactionFiltersToQuery,
		fromQuery: transactionFiltersFromQuery,
	})
	const [params, setParams] = useSearch<TTransactionFilters>(query)
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])
	return (
		<Fragment>
			<Search
				initialParams={params}
				onSearch={(params) => setParams({ ...params, cursor: null })}
				title="Transactions"
				placeholder="Search by txn id..."
				Filters={TransactionsFilters}
				resetParams={(params) => ({
					active_tab: params.active_tab,
				})}
				areAdvancedFiltersApplied={(params) => {
					const { successful_only, active_tab, after, before, ...others } =
						params
					return (
						isTruthy(successful_only) ||
						Boolean(active_tab === "all" ? after || before : false) ||
						areAdvancedFiltersAppliedDefault(others)
					)
				}}
			>
				{({ searchParams, setSearchParamValue }) => {
					return (
						<Tabs>
							<TabsList>
								<TabItem
									active={searchParams.active_tab === "today"}
									onClick={() => setSearchParamValue("active_tab", "today")}
								>
									Today
								</TabItem>
								<TabItem
									active={searchParams.active_tab === "yesterday"}
									onClick={() => setSearchParamValue("active_tab", "yesterday")}
								>
									Yesterday
								</TabItem>
								<TabItem
									active={searchParams.active_tab === "this_month"}
									onClick={() =>
										setSearchParamValue("active_tab", "this_month")
									}
								>
									This Month
								</TabItem>
								<TabItem
									active={searchParams.active_tab === "last_month"}
									onClick={() =>
										setSearchParamValue("active_tab", "last_month")
									}
								>
									Last Month
								</TabItem>
								<TabItem
									active={searchParams.active_tab === "all"}
									onClick={() => setSearchParamValue("active_tab", "all")}
								>
									All
								</TabItem>
							</TabsList>
							<TabContent>
								<Stack>
									<Container fluid paddingY="4">
										<Box
											padding="4"
											bgColor="primary"
											borderWidth="1"
											borderColor="primary"
											rounded="md"
										>
											<TransactionsWidget filters={params} appId={appId} />
										</Box>
									</Container>
									<CursorListView<TPGTransaction, TTransactionFilters>
										pageKey={`pg/${appId}/transactions`}
										params={params}
										fetch={(xhr, params) =>
											xhr
												.get(`/pg-transactions`, {
													params: transactionFiltersToRequestParams(
														params,
														appId
													),
												})
												.then((resp) => resp.data)
										}
										onCursorChange={(cursor) => {
											setParams({ ...params, cursor })
										}}
									>
										{({ items }) => (
											<Table
												bordered
												striped
												responsive
												headers={[
													"PG Ref ID",
													"For",
													"Txn. Date",
													"Req. Amount",
													"Method",
													"Txn. Amount",
													"Conv. Fee+Tax",
													"Merc. Fee",
													"Merc. Tax",
													"Setl. Date",
													"Setl. Amount",
												]}
												alignCols={{
													2: "center",
													3: "right",
													4: "center",
													5: "right",
													6: "right",
													7: "right",
													8: "right",
													9: "center",
													10: "right",
												}}
												rows={items.map((transaction) => [
													<Stack>
														<Text>{transaction.pg_provider_reference_id}</Text>
														{!transaction.verified_at ? (
															<Box>
																<Badge status="warning">
																	Pending Verification
																</Badge>
															</Box>
														) : null}
													</Stack>,
													transaction.invoice ? (
														transaction.invoice.invoiceable_type ===
														"given_quotes" ? (
															<Link
																to={generatePath(
																	"/trips/:tripId/quotes/:quoteId",
																	{
																		tripId:
																			transaction.invoice.invoiceable.trip_quote.trip_id.toString(),
																		quoteId:
																			transaction.invoice.invoiceable.trip_quote_id.toString(),
																	}
																)}
																color="accent"
																fontWeight="semibold"
															>
																Trip #
																{
																	transaction.invoice.invoiceable.trip_quote
																		.trip_id
																}
															</Link>
														) : transaction.invoice.invoiceable_type ===
														  "instalments" ? (
															<Link
																to={generatePath(
																	"/payments/instalments/:instalmentId",
																	{
																		instalmentId:
																			transaction.invoice.invoiceable.id.toString(),
																	}
																)}
																color="accent"
																fontWeight="semibold"
															>
																Instl #{transaction.invoice.invoiceable_id}
															</Link>
														) : (
															<Text>ERROR</Text>
														)
													) : null,
													<Time
														timestamp={transaction.date}
														format="DD MMM [at] HH:mm"
													/>,
													transaction.invoice ? (
														<Stack gap="1">
															<Money
																money={transaction.invoice.amount}
																showCurrency
															/>
															{transaction.invoice.original_currency !==
															transaction.invoice.currency ? (
																<Money
																	money={transaction.invoice.original_amount}
																	showCurrency
																	color="muted"
																	fontSize="sm"
																/>
															) : null}
														</Stack>
													) : null,
													transaction.method ? (
														<Badge>{transaction.method}</Badge>
													) : (
														<Text color="muted">...</Text>
													),
													<Box>
														{transaction.status !== "success" &&
														transaction?.pg_failure ? (
															<Tooltip
																content={
																	<Stack gap="2">
																		<Stack gap="2" flexWrap="wrap">
																			<Text>
																				Failure Category:{" "}
																				{
																					transaction.pg_failure
																						.failure_category
																				}
																			</Text>
																			{transaction.pg_failure
																				?.pg_provider_error_message ? (
																				<Text>
																					Failure Reason:{" "}
																					{
																						transaction.pg_failure
																							?.pg_provider_error_message
																					}
																				</Text>
																			) : null}
																		</Stack>
																	</Stack>
																}
															>
																<Box>
																	<Badge status="warning">
																		{transaction.status}
																	</Badge>
																</Box>
															</Tooltip>
														) : (
															<Money
																money={transaction.amount}
																keepTrailingZero
																showCurrency
																color={
																	transaction.verified_at
																		? "success"
																		: undefined
																}
															/>
														)}
													</Box>,
													transaction.setl_date ? (
														transaction.fee_and_tax_bifurcation &&
														!isZeroMoney(
															transaction.fee_and_tax_bifurcation
																.convenience_fee
														) ? (
															<Money
																money={addMoney(
																	transaction.fee_and_tax_bifurcation
																		.convenience_fee,
																	transaction.fee_and_tax_bifurcation
																		.convenience_tax
																)}
															/>
														) : (
															0
														)
													) : (
														"..."
													),
													transaction.fee_and_tax_bifurcation ? (
														<Money
															money={
																transaction.fee_and_tax_bifurcation.merchant_fee
															}
														/>
													) : (
														"..."
													),
													transaction.fee_and_tax_bifurcation ? (
														<Money
															money={
																transaction.fee_and_tax_bifurcation.merchant_tax
															}
														/>
													) : (
														"..."
													),
													transaction.setl_date ? (
														<Time
															value={transaction.setl_date}
															format="DD MMM"
														/>
													) : (
														"..."
													),
													transaction.status !== "success" ? null : transaction
															.settlement_entries?.length ? (
														<Stack>
															{transaction.setl_credit_amount &&
															!isZeroMoney(transaction.setl_credit_amount) ? (
																<Money
																	money={transaction.setl_credit_amount}
																	keepTrailingZero
																	showCurrency
																	color="success"
																/>
															) : null}
															{transaction.setl_debit_amount &&
															!isZeroMoney(transaction.setl_debit_amount) ? (
																<Money
																	money={transaction.setl_debit_amount}
																	keepTrailingZero
																	showCurrency
																	color="danger"
																/>
															) : null}
														</Stack>
													) : (
														<Badge warning>Pending</Badge>
													),
												])}
											/>
										)}
									</CursorListView>
								</Stack>
							</TabContent>
						</Tabs>
					)
				}}
			</Search>
		</Fragment>
	)
}

function transactionFiltersToRequestParams(
	filters: TTransactionFilters,
	appId: string | number | undefined
) {
	const { after, before, active_tab, tenant, ...params } = filters
	let afterDate = after
	let beforeDate = before
	switch (active_tab) {
		case "today":
			afterDate = startOf(new Date(), "day")
			beforeDate = endOf(new Date(), "day")
			break
		case "yesterday":
			afterDate = startOf(subtractUnit(new Date(), 1, "day"), "day")
			beforeDate = endOf(subtractUnit(new Date(), 1, "day"), "day")
			break
		case "this_month":
			afterDate = startOf(new Date(), "month")
			beforeDate = endOf(new Date(), "month")
			break
		case "last_month":
			afterDate = startOf(subtractUnit(new Date(), 1, "month"), "month")
			beforeDate = endOf(subtractUnit(new Date(), 1, "month"), "month")
			break
	}
	return {
		...params,
		after: afterDate ? dateToUTCString(startOf(afterDate, "day")) : null,
		before: beforeDate ? dateToUTCString(endOf(beforeDate, "day")) : null,
		app_ids: appId ? [appId] : [],
		tenant: tenant?.id,
	}
}

function SelectTenantWithPGIntegration(
	props: Omit<React.ComponentProps<typeof AsyncSelect>, "fetch">
) {
	const xhr = useXHR()
	return (
		<AsyncSelect
			{...props}
			perFetchLimit={20}
			fetch={(q, params) =>
				xhr
					.get<IListResponse<{ id: number; name: string }>>(
						"/integrations/pg/tenants",
						{
							params: {
								q,
								limit: 20,
								...params,
							},
						}
					)
					.then((resp) => resp.data.data)
			}
		/>
	)
}

function TransactionsFilters() {
	const { value: activeTab } =
		useFieldValue<TTransactionFilters["active_tab"]>("active_tab")
	const { value: successfulOnly } = useFieldValue<boolean>("successful_only")
	const { value: viewAllFailures, onChange: setViewAllFailures } =
		useFieldValue<boolean>("view_all_failures")
	const { hasPermission } = useCheckPermissions()

	useEffect(() => {
		if (isTruthy(successfulOnly) && isTruthy(viewAllFailures)) {
			setViewAllFailures(false)
		}
	}, [successfulOnly, viewAllFailures, setViewAllFailures])

	return (
		<Stack gap="4">
			{hasPermission(PERMISSIONS.MANAGE_TENANTS) ? (
				<SelectField
					select={SelectTenantWithPGIntegration}
					name="tenant"
					label="Tenant"
					fetchOnMount
				/>
			) : null}
			{activeTab === "all" ? (
				<DateRangePickerField label="During" fromName="after" toName="before" />
			) : null}
			<SwitchInputField name="successful_only" label="Successful Txns Only" />
			<SwitchInputField
				name="view_all_failures"
				label="Include All Failure Txns"
				disabled={isTruthy(successfulOnly)}
			/>
		</Stack>
	)
}

interface IPGTransactionsStats {
	successful_count: number
	failure_count: number
	failure: {
		count: number
		actual_failure: {
			count: number
			payment_declined: number
			payment_authentication_failed: number
			payment_technical_error: number
		}
		customer_failure: {
			count: number
			payment_timeout: number
			payment_cancelled: number
		}
	}
	unverified_count: number
	successful_amount: Array<TMoney>
}
function TransactionsWidget({
	filters,
	appId,
}: {
	filters: TTransactionFilters
	appId: number | string | undefined
}) {
	const xhr = useXHR()
	const { data } = useSWR(
		"pg-transactions/stats" + queryToSearch(transactionFiltersToQuery(filters)),
		() =>
			xhr
				.get<{
					data: IPGTransactionsStats
				}>("pg-transactions/stats", {
					params: transactionFiltersToRequestParams(filters, appId),
				})
				.then((resp) => resp.data.data)
	)

	return (
		<Inline gap="4" flexWrap="wrap">
			<Stack gap="1">
				<Text fontWeight="semibold" color="muted">
					Amount
				</Text>
				<Text fontSize="lg" fontWeight="semibold">
					{data ? <MoneySum money={data.successful_amount} /> : "..."}
				</Text>
			</Stack>
			<Box borderLeftWidth="2" height="auto" alignSelf="stretch" as="div" />
			<Stack gap="1">
				<Text fontWeight="semibold" color="muted">
					Successful
				</Text>
				<Text fontSize="lg" fontWeight="semibold">
					{data ? data.successful_count : "..."}
				</Text>
			</Stack>
			<Box borderLeftWidth="2" height="auto" alignSelf="stretch" as="div" />
			<Tooltip
				content={
					<Stack gap="2">
						<Stack gap="1" flexWrap="wrap">
							<Inline gap="1" justifyContent="between">
								<Text>Payment Declined</Text>
								<Text fontWeight="semibold">
									{data?.failure.actual_failure.payment_declined ?? "..."}
								</Text>
							</Inline>
							<Inline gap="1" justifyContent="between">
								<Text>Payment Authentication Failed</Text>
								<Text fontWeight="semibold">
									{data?.failure.actual_failure.payment_authentication_failed ??
										"..."}
								</Text>
							</Inline>
							<Inline gap="1" justifyContent="between">
								<Text>Payment Technical Error</Text>
								<Text fontWeight="semibold">
									{data?.failure.actual_failure.payment_technical_error ??
										"..."}
								</Text>
							</Inline>
						</Stack>
					</Stack>
				}
			>
				<Stack
					gap="1"
					alignItems="start"
					justifyContent="between"
					cursor="pointer"
				>
					<Text fontWeight="semibold" color="muted">
						Failure <Icons.Info />
					</Text>
					<Text fontSize="lg" fontWeight="semibold" color="danger">
						{data ? data.failure.actual_failure.count : "..."}
					</Text>
				</Stack>
			</Tooltip>
			<Tooltip
				content={
					<Stack gap="2">
						<Stack gap="1">
							<Inline
								alignItems="start"
								gap="1"
								width="full"
								justifyContent="between"
							>
								<Text>Payment Timeout</Text>
								<Inline alignItems="center" gap="1">
									<Text fontWeight="semibold">
										{data?.failure.customer_failure.payment_timeout ?? "..."}
									</Text>
								</Inline>
							</Inline>
							<Inline
								alignItems="start"
								justifyContent="between"
								gap="1"
								width="full"
							>
								<Text>Payment Cancelled</Text>
								<Inline alignItems="center" gap="1">
									<Text fontWeight="semibold">
										{data?.failure.customer_failure.payment_cancelled ?? "..."}
									</Text>
								</Inline>
							</Inline>
						</Stack>
					</Stack>
				}
			>
				<Stack
					gap="1"
					alignItems="start"
					justifyContent="between"
					cursor="pointer"
				>
					<Text fontWeight="semibold" color="muted">
						Cancelled <Icons.Info />
					</Text>
					<Text fontSize="lg" fontWeight="semibold" color="danger">
						{data ? data.failure.customer_failure.count : "..."}
					</Text>
				</Stack>
			</Tooltip>
			{data && data.unverified_count ? (
				<>
					<Box borderLeftWidth="2" height="auto" alignSelf="stretch" as="div" />
					<Stack gap="1">
						<Text fontWeight="semibold" color="muted">
							Unverified
						</Text>
						<Text fontSize="lg" fontWeight="semibold">
							{data.unverified_count}
						</Text>
					</Stack>
				</>
			) : null}
		</Inline>
	)
}

function SettlementsWidget({
	filters,
	appId,
}: {
	filters: TSettlementFilters
	appId: number | string | undefined
}) {
	const xhr = useXHR()
	const { data } = useSWR(
		"pg-settlements/stats" + queryToSearch(transactionFiltersToQuery(filters)),
		() =>
			xhr
				.get<{
					data: {
						credit_amount: Array<TMoney>
						debit_amount: Array<TMoney>
						total_count: number
					}
				}>("pg-settlements/stats", {
					params: settlementFiltersToRequestParams(filters, appId),
				})
				.then((resp) => resp.data.data)
	)

	if (!data) {
		return (
			<Inline gap="4" flexWrap="wrap">
				<Stack gap="1">
					<Text fontWeight="semibold" color="muted">
						Amount
					</Text>
					<Text fontSize="lg" fontWeight="semibold">
						...
					</Text>
				</Stack>
			</Inline>
		)
	}

	return (
		<Inline gap="4" flexWrap="wrap">
			{data.credit_amount.length ? (
				<Stack gap="1" color="success">
					<Text fontWeight="semibold" color="muted">
						Credit Amount
					</Text>
					<Text fontSize="lg" fontWeight="semibold">
						<MoneySum money={data.credit_amount} />
					</Text>
				</Stack>
			) : null}
			{data.debit_amount.length ? (
				<Stack gap="1" color="danger">
					<Text fontWeight="semibold" color="muted">
						Debit Amount
					</Text>
					<Text fontSize="lg" fontWeight="semibold">
						<MoneySum money={data.debit_amount} />
					</Text>
				</Stack>
			) : null}
			<Stack gap="1">
				<Text fontWeight="semibold" color="muted">
					Count
				</Text>
				<Text fontSize="lg" fontWeight="semibold">
					{data.total_count}
				</Text>
			</Stack>
		</Inline>
	)
}

type TSettlementFilters = TSearchParams & {
	after?: Date
	before?: Date
	active_tab: "today" | "yesterday" | "this_month" | "last_month" | "all"
	tenant?: TTenant
}

type TSettlementFiltersInQuery = TSearchParams & {
	after?: string
	before?: string
	active_tab?: TSettlementFilters["active_tab"]
	tenant?: string
}

function settlementFiltersToQuery(
	filter: TSettlementFilters
): TSettlementFiltersInQuery {
	const { q, page, after, before, active_tab, tenant } = filter
	const query: TSettlementFiltersInQuery = { active_tab }
	if (q) {
		query.q = q
	}
	if (page) {
		query.page = page
	}
	if (active_tab === "all") {
		if (after) {
			query.after = dateToQuery(after)
		}
		if (before) {
			query.before = dateToQuery(before)
		}
	}
	if (tenant) {
		query.tenant = `${tenant.id}_${tenant.name}`
	}
	return query
}

function settlementFiltersFromQuery(
	query: TSettlementFiltersInQuery
): TSettlementFilters {
	const { q, page, after, before, active_tab, tenant } = query
	const filters: TSettlementFilters = { active_tab: active_tab || "today" }

	if (q) {
		filters.q = q
	}
	if (page) {
		filters.page = page
	}
	if (query.active_tab === "all") {
		if (after) {
			filters.after = parseDateFromQuery(after)
		}
		if (before) {
			filters.before = parseDateFromQuery(before)
		}
	}
	if (tenant) {
		const [id, ...name] = tenant.split("_")
		filters.tenant = {
			id: Number(id),
			name: name.join("_"),
		}
	}
	return filters
}

export function SettlementsList({ appId }: { appId?: number }) {
	const [query, setQuery] = useLocationQuery<
		TSettlementFilters,
		TSettlementFiltersInQuery
	>({
		toQuery: settlementFiltersToQuery,
		fromQuery: settlementFiltersFromQuery,
	})
	const [refreshCounter, setRefreshCounter] = useState(0)
	const [params, setParams] = useSearch<TSettlementFilters>(query)
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])
	return (
		<Fragment key={refreshCounter}>
			<Search
				initialParams={params}
				onSearch={(params) => setParams({ ...params, cursor: null })}
				title="Settlements"
				placeholder="Search by utr or txn id..."
				Filters={SettlementsFilters}
				resetParams={(params) => ({
					active_tab: params.active_tab,
				})}
				areAdvancedFiltersApplied={(params) => {
					const { active_tab, after, before, ...others } = params
					return (
						Boolean(active_tab === "all" ? after || before : false) ||
						areAdvancedFiltersAppliedDefault(others)
					)
				}}
				actions={
					appId ? (
						<Inline gap="4">
							<Box>
								<Batch
									onFinish={() => {
										showSnackbar("Settlements Fetched Successfully")
										setRefreshCounter((c) => c + 1)
									}}
								>
									{({ init, xhr, inProgress }) => (
										<Dropdown alignRight>
											<Dropdown.ToggleButton>
												<Icons.DotsVertical />
											</Dropdown.ToggleButton>
											<Dropdown.Menu>
												<Dropdown.MenuItem
													onClick={async (e: React.MouseEvent) => {
														e.preventDefault()
														init(
															await xhr
																.post<{
																	data: TBatch
																}>(`pg-settlements/fetch-from-remote`, {
																	app_ids: [appId],
																})
																.then((resp) => resp.data.data)
														)
													}}
												>
													{inProgress ? "Fetching..." : "Re-Fetch last 7 days"}
												</Dropdown.MenuItem>
											</Dropdown.Menu>
										</Dropdown>
									)}
								</Batch>
							</Box>
						</Inline>
					) : null
				}
			>
				{({ searchParams, setSearchParamValue }) => {
					return (
						<Tabs>
							<TabsList>
								<TabItem
									active={searchParams.active_tab === "today"}
									onClick={() => setSearchParamValue("active_tab", "today")}
								>
									Today
								</TabItem>
								<TabItem
									active={searchParams.active_tab === "yesterday"}
									onClick={() => setSearchParamValue("active_tab", "yesterday")}
								>
									Yesterday
								</TabItem>
								<TabItem
									active={searchParams.active_tab === "this_month"}
									onClick={() =>
										setSearchParamValue("active_tab", "this_month")
									}
								>
									This Month
								</TabItem>
								<TabItem
									active={searchParams.active_tab === "last_month"}
									onClick={() =>
										setSearchParamValue("active_tab", "last_month")
									}
								>
									Last Month
								</TabItem>
								<TabItem
									active={searchParams.active_tab === "all"}
									onClick={() => setSearchParamValue("active_tab", "all")}
								>
									All
								</TabItem>
							</TabsList>
							<TabContent>
								<Stack>
									<Container fluid paddingY="4">
										<Box
											padding="4"
											bgColor="primary"
											borderWidth="1"
											borderColor="primary"
											rounded="md"
										>
											<SettlementsWidget filters={params} appId={appId} />
										</Box>
									</Container>
									<CursorListView<TPGSettlement, TSettlementFilters>
										pageKey={`pg/${appId}/settlements`}
										params={params}
										fetch={(xhr, params) =>
											xhr
												.get(`/pg-settlements`, {
													params: settlementFiltersToRequestParams(
														params,
														appId
													),
												})
												.then((resp) => resp.data)
										}
										actions={
											<TemporaryStorage
												initUrl="/pg-settlements/download"
												initParams={settlementFiltersToRequestParams(
													params,
													appId
												)}
											>
												{({
													message,
													generate,
													download,
													isGenerating,
													url,
													reset,
												}) => (
													<Dropdown alignRight>
														<Dropdown.ToggleButton size="sm">
															<Icons.DotsVertical />
														</Dropdown.ToggleButton>
														<Dropdown.Menu>
															{url ? (
																<Dropdown.MenuItem
																	download
																	href={url}
																	onClick={() => reset()}
																>
																	{message}
																</Dropdown.MenuItem>
															) : (
																<Dropdown.MenuItem
																	onClick={(e) => {
																		if (isGenerating) return
																		e.preventDefault()
																		generate()
																	}}
																>
																	{message || (
																		<>
																			<Icons.DocumentDownload /> Download
																		</>
																	)}
																</Dropdown.MenuItem>
															)}
														</Dropdown.Menu>
													</Dropdown>
												)}
											</TemporaryStorage>
										}
										onCursorChange={(cursor) => {
											setParams({ ...params, cursor })
										}}
									>
										{({ items }) => (
											<Table
												bordered
												striped
												responsive
												headers={[
													"UTR",
													"Date",
													"Req. Amount",
													"Txn. Amount",
													"Conv. Fee+Tax",
													"Merc. Fee",
													"Merc. Tax",
													"Setl. Amount",
													"Entries",
												]}
												alignCols={{
													1: "center",
													2: "right",
													3: "right",
													4: "right",
													5: "right",
													6: "right",
													7: "right",
													8: "right",
												}}
												rows={items.map(
													({ fee_and_tax_bifurcation, ...settlement }) => [
														settlement.utr,
														<Time timestamp={settlement.created_at}>
															{formatDate(parseDate(settlement.date), "DD MMM")}
														</Time>,
														settlement.request_amount ? (
															<Inline gap="1" justifyContent="end">
																{settlement.unlinked_entries_count ? (
																	<Box>
																		<Icons.AttentionSolid
																			color="warning"
																			title={`${settlement.unlinked_entries_count} ${pluralize("settlement", settlement.unlinked_entries_count)} ${pluralize("is", settlement.unlinked_entries_count)} not linked with any system generated payments.`}
																		/>
																	</Box>
																) : null}
																<Money
																	money={settlement.request_amount}
																	showCurrency
																/>
															</Inline>
														) : null,
														settlement.txn_amount ? (
															<Money
																money={settlement.txn_amount}
																keepTrailingZero
																showCurrency
															/>
														) : null,

														fee_and_tax_bifurcation ? (
															!isZeroMoney(
																fee_and_tax_bifurcation.convenience_fee
															) ? (
																<Money
																	money={addMoney(
																		fee_and_tax_bifurcation.convenience_fee,
																		fee_and_tax_bifurcation.convenience_tax
																	)}
																/>
															) : (
																"0"
															)
														) : (
															<Text color="muted">N/A</Text>
														),
														fee_and_tax_bifurcation ? (
															<Money
																money={fee_and_tax_bifurcation.merchant_fee}
															/>
														) : (
															<Text color="muted">N/A</Text>
														),
														fee_and_tax_bifurcation ? (
															<Money
																money={fee_and_tax_bifurcation.merchant_tax}
															/>
														) : (
															<Text color="muted">N/A</Text>
														),
														<Stack>
															{settlement.credit_amount &&
															!isZeroMoney(settlement.credit_amount) ? (
																<Money
																	money={settlement.credit_amount}
																	keepTrailingZero
																	showCurrency
																	color="success"
																/>
															) : null}
															{settlement.debit_amount &&
															!isZeroMoney(settlement.debit_amount) ? (
																<Money
																	money={settlement.debit_amount}
																	keepTrailingZero
																	showCurrency
																	color="danger"
																/>
															) : null}
														</Stack>,
														settlement.entries?.length ? (
															<Component initialState={false}>
																{({ state, setState }) => (
																	<>
																		<Button onClick={() => setState(true)}>
																			{settlement.entries?.length}{" "}
																			<Icons.ArrowExpand />
																		</Button>
																		<Dialog
																			title="Settled Transactions"
																			onClose={() => setState(false)}
																			open={state}
																			fitContainer
																		>
																			<Dialog.Body>
																				<SettlementDetails
																					settlementId={settlement.id}
																				/>
																			</Dialog.Body>
																			<Dialog.Footer>
																				<Button onClick={() => setState(false)}>
																					Ok
																				</Button>
																			</Dialog.Footer>
																		</Dialog>
																	</>
																)}
															</Component>
														) : null,
													]
												)}
											/>
										)}
									</CursorListView>
								</Stack>
							</TabContent>
						</Tabs>
					)
				}}
			</Search>
		</Fragment>
	)
}

function settlementFiltersToRequestParams(
	filters: TSettlementFilters,
	appId: string | number | undefined
) {
	const { after, before, active_tab, tenant, ...params } = filters
	let afterDate = after
	let beforeDate = before
	switch (active_tab) {
		case "today":
			afterDate = startOf(new Date(), "day")
			beforeDate = endOf(new Date(), "day")
			break
		case "yesterday":
			afterDate = startOf(subtractUnit(new Date(), 1, "day"), "day")
			beforeDate = endOf(subtractUnit(new Date(), 1, "day"), "day")
			break
		case "this_month":
			afterDate = startOf(new Date(), "month")
			beforeDate = endOf(new Date(), "month")
			break
		case "last_month":
			afterDate = startOf(subtractUnit(new Date(), 1, "month"), "month")
			beforeDate = endOf(subtractUnit(new Date(), 1, "month"), "month")
			break
	}
	return {
		...params,
		after: afterDate ? dateToQuery(startOf(afterDate, "day")) : null,
		before: beforeDate ? dateToQuery(endOf(beforeDate, "day")) : null,
		app_ids: appId ? [appId] : [],
		tenant: tenant?.id,
	}
}

function SettlementsFilters() {
	const { value: activeTab } =
		useFieldValue<TSettlementFilters["active_tab"]>("active_tab")
	const { hasPermission } = useCheckPermissions()
	return (
		<Stack gap="4">
			{hasPermission(PERMISSIONS.MANAGE_TENANTS) ? (
				<SelectField
					select={SelectTenantWithPGIntegration}
					name="tenant"
					label="Tenant"
					fetchOnMount
				/>
			) : null}
			{activeTab === "all" ? (
				<DateRangePickerField label="During" fromName="after" toName="before" />
			) : null}
		</Stack>
	)
}

function SettlementDetails({ settlementId }: { settlementId: number }) {
	const xhr = useXHR()
	const { data: settlement, error } = useSWR(
		`settlements/${settlementId}`,
		() =>
			xhr
				.get<{ data: TPGSettlement }>(`/pg-settlements/${settlementId}`)
				.then((resp) => resp.data.data)
	)
	if (!settlement) {
		if (error)
			return (
				<Alert status="error">
					{error.message || "Something went wrong. Please retry."}
				</Alert>
			)
		return <Spinner alignCenter padding="4" />
	}
	return (
		<Stack gap="4">
			<Inline
				padding="4"
				rounded="md"
				borderWidth="1"
				gap="8"
				flexWrap="wrap"
				bgColor="primary"
				borderColor="primary"
			>
				<Stack gap="px">
					<Text color="muted">Date</Text>
					<Text fontWeight="semibold">
						{formatDate(settlement.date, "DD MMM, YYYY")}
					</Text>
				</Stack>
				<Stack gap="px">
					<Text color="muted">UTR</Text>
					<Text fontWeight="semibold">{settlement.utr}</Text>
				</Stack>
				{settlement.request_amount ? (
					<Stack gap="px">
						<Inline gap="1">
							<Text color="muted">Req. Amount</Text>
							{settlement.unlinked_entries_count ? (
								<Box>
									<Icons.AttentionSolid
										color="warning"
										title={`${settlement.unlinked_entries_count} ${pluralize("settlement", settlement.unlinked_entries_count)} ${pluralize("is", settlement.unlinked_entries_count)} not linked with any system generated payments.`}
									/>
								</Box>
							) : null}
						</Inline>
						<Text fontWeight="semibold">
							<Money
								money={settlement.request_amount}
								showCurrency
								keepTrailingZero
							/>
						</Text>
					</Stack>
				) : null}
				{settlement.txn_amount ? (
					<Stack gap="px">
						<Text color="muted">Txn. Amount</Text>
						<Text>
							<Money
								money={settlement.txn_amount}
								showCurrency
								keepTrailingZero
							/>
						</Text>
					</Stack>
				) : null}
				{settlement.fee_and_tax_bifurcation ? (
					<>
						<Stack gap="px">
							<Text color="muted">Conv. Fee+Tax</Text>
							<Text>
								{!isZeroMoney(
									settlement.fee_and_tax_bifurcation.convenience_fee
								) ? (
									<Money
										money={addMoney(
											settlement.fee_and_tax_bifurcation.convenience_fee,
											settlement.fee_and_tax_bifurcation.convenience_tax
										)}
										showCurrency
									/>
								) : (
									"0"
								)}
							</Text>
						</Stack>
						<Stack gap="px">
							<Text color="muted">Merc. Fee</Text>
							<Text fontWeight="semibold">
								<Money
									money={settlement.fee_and_tax_bifurcation.merchant_fee}
									showCurrency
								/>
							</Text>
						</Stack>
						<Stack gap="px">
							<Text color="muted">Merc. Tax</Text>
							<Text>
								<Money
									money={settlement.fee_and_tax_bifurcation.merchant_tax}
									showCurrency
								/>
							</Text>
						</Stack>
					</>
				) : null}
				{settlement.debit_amount && !isZeroMoney(settlement.debit_amount) ? (
					<Stack gap="px">
						<Text color="muted">Debit Amount</Text>
						<Text fontWeight="semibold">
							<Money
								money={settlement.debit_amount}
								keepTrailingZero
								showCurrency
							/>
						</Text>
					</Stack>
				) : null}
				{settlement.credit_amount && !isZeroMoney(settlement.credit_amount) ? (
					<Stack gap="px">
						<Text color="muted">Credit Amount</Text>
						<Text fontWeight="semibold">
							<Money
								money={settlement.credit_amount}
								keepTrailingZero
								showCurrency
							/>
						</Text>
					</Stack>
				) : null}
			</Inline>
			{!settlement.entries?.length ? (
				<Alert title="No entries in this settlement" status="warning">
					There are no entries in this settlement. This is not expected. Please
					check with payment gateway provider for more details.
				</Alert>
			) : (
				<Stack gap="2">
					<Stack gap="1">
						<Heading fontSize="md">Settled Transactions</Heading>
						<Text>
							Below are {settlement.entries?.length || 0} transaction{" "}
							{pluralize("entries", settlement.entries?.length || 0)} settled in
							this settlement.
						</Text>
					</Stack>
					<Table
						responsive
						headers={[
							"PG Txn ID",
							"Txn Date",
							"Req. Amount",
							"Method",
							"Txn Amount",
							"Setl. Amount",
							"Conv. Fee+Tax",
							"Merc. Fee",
							"Merc. Tax",
							"Linked Entry",
						]}
						alignCols={{
							1: "center",
							2: "right",
							3: "center",
							4: "right",
							5: "right",
							6: "right",
							7: "right",
							8: "right",
						}}
						bordered
						striped
						rows={settlement.entries.map(
							({
								pg_provider_transaction_reference_id,
								txn_date,
								txn_amount,
								txn_method,
								credit_amount,
								debit_amount,
								transaction,
								fee_and_tax_bifurcation,
							}) => {
								const invoice = transaction?.invoice
								return [
									pg_provider_transaction_reference_id,
									<Time timestamp={txn_date} format="D MMM [at] HH:mm" />,
									<Stack>
										{invoice ? (
											<Stack gap="1">
												<Money money={invoice.amount} showCurrency />
												{invoice.original_currency !== invoice.currency ? (
													<Money
														money={invoice.original_amount}
														showCurrency
														color="muted"
														fontSize="sm"
													/>
												) : null}
											</Stack>
										) : (
											<Text title="Settlement not linked with any system generated payments">
												<Icons.AttentionSolid color="warning" />
											</Text>
										)}
										{!transaction?.verified_at ? (
											<Box>
												<Badge status="warning">Unverified</Badge>
											</Box>
										) : null}
									</Stack>,
									<Badge>{txn_method}</Badge>,
									<Money money={txn_amount} keepTrailingZero />,
									<Stack>
										{!isZeroMoney(credit_amount) ? (
											<Money
												money={credit_amount}
												keepTrailingZero
												color="success"
											/>
										) : null}
										{!isZeroMoney(debit_amount) ? (
											<Money
												money={debit_amount}
												keepTrailingZero
												showCurrency
												color="danger"
											/>
										) : null}
									</Stack>,
									fee_and_tax_bifurcation ? (
										!isZeroMoney(fee_and_tax_bifurcation.convenience_fee) ? (
											<Money
												money={addMoney(
													fee_and_tax_bifurcation.convenience_fee,
													fee_and_tax_bifurcation.convenience_tax
												)}
											/>
										) : (
											"0"
										)
									) : (
										<Text color="muted">N/A</Text>
									),
									fee_and_tax_bifurcation ? (
										<Money money={fee_and_tax_bifurcation.merchant_fee} />
									) : (
										<Text color="muted">N/A</Text>
									),
									fee_and_tax_bifurcation ? (
										<Money money={fee_and_tax_bifurcation.merchant_tax} />
									) : (
										<Text color="muted">N/A</Text>
									),
									invoice ? (
										invoice.invoiceable_type === "given_quotes" ? (
											<Link
												key={invoice.id}
												to={generatePath("/trips/:tripId/quotes/:quoteId", {
													tripId:
														invoice.invoiceable.trip_quote.trip_id.toString(),
													quoteId: invoice.invoiceable.trip_quote_id.toString(),
												})}
												color="accent"
												fontWeight="semibold"
											>
												Trip #{invoice.invoiceable.trip_quote.trip_id}
											</Link>
										) : invoice.invoiceable_type === "instalments" ? (
											<Link
												key={invoice.id}
												to={generatePath(
													"/payments/instalments/:instalmentId",
													{
														instalmentId: invoice.invoiceable.id.toString(),
													}
												)}
												color="accent"
												fontWeight="semibold"
											>
												Instl #{invoice.invoiceable_id}
											</Link>
										) : (
											<Alert inline status="error">
												Please Report: {transaction.invoice?.invoiceable_type}
											</Alert>
										)
									) : (
										<Text color="muted">Not Linked</Text>
									),
								]
							}
						)}
					/>
				</Stack>
			)}
		</Stack>
	)
}

export function IsPGPresent({
	currency,
	children,
}: {
	currency: string
	children: ReactNode
}) {
	const pgIntegratedAppsCount = usePaymentGatewayIntegratedAppsCount()
	if (pgIntegratedAppsCount) return <>{children}</>
	else return null
}
